<template>
  <div class="hms-page">
    <Navigation />

    <div class="container-fluid hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/patients')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-folder-account-outline me-2"></i>{{ $t('page.patient_chart', 'Patient Chart') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap" v-if="patient">
          <button class="hms-btn hms-btn-primary" @click="goEdit" title="Edit demographics, allergies, NoK, insurance">
            <i class="mdi mdi-pencil me-1"></i>Edit
          </button>
          <button class="hms-btn hms-btn-green" :disabled="resendingWelcome" @click="doResendWelcome"
                  :title="(patient.phone || patient.email) ? '' : 'No phone or email on file'">
            <i class="mdi" :class="resendingWelcome ? 'mdi-loading mdi-spin' : 'mdi-message-text-fast-outline'"></i>
            {{ resendingWelcome ? ' Sending…' : ' Resend Welcome' }}
          </button>
          <button class="hms-btn hms-btn-blue" @click="$router.push('/billing?patient_id=' + patient.patient_id)" title="Create a new invoice for this patient">
            <i class="mdi mdi-receipt-text-plus me-1"></i>New Invoice
          </button>
          <button class="hms-btn hms-btn-blue" @click="$router.push('/queue')" title="Issue queue ticket">
            <i class="mdi mdi-ticket-plus me-1"></i>Queue
          </button>
          <button class="hms-btn hms-btn-blue" @click="$router.push('/consultation')">
            <i class="mdi mdi-doctor me-1"></i>Consultation
          </button>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading" title="Refresh">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <div v-if="loading && !patient" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else-if="!patient" class="hms-card hms-empty">
        <i class="mdi mdi-account-question-outline"></i>Patient not found.
      </div>

      <div v-else class="row g-3">
        <!-- LEFT — Identity -->
        <div class="col-lg-3">
          <div class="hms-card hms-identity">
            <div class="hms-avatar-xl">{{ initials }}</div>
            <div class="hms-id-name">
              {{ patient.first_name }} {{ patient.last_name }}<span v-if="patient.other_names"> {{ patient.other_names }}</span>
              <button class="hms-copy-btn" @click="copyText(([patient.first_name, patient.other_names, patient.last_name].filter(Boolean).join(' ')), 'Name')" title="Copy name"><i class="mdi mdi-content-copy"></i></button>
            </div>
            <div class="hms-muted-sm hms-mono hms-copyable">
              {{ patient.patient_no }}
              <button class="hms-copy-btn" @click="copyText(patient.patient_no, 'Patient No')" title="Copy patient no"><i class="mdi mdi-content-copy"></i></button>
            </div>
            <div class="hms-id-row mt-2">
              <span class="hms-badge hms-badge-info">{{ ageStr }}</span>
              <span class="hms-badge hms-badge-info">{{ patient.gender }}</span>
              <span class="hms-badge hms-badge-purple" v-if="patient.blood_group">BG {{ patient.blood_group }}</span>
            </div>

            <!-- ============================================================
              CONFIDENTIAL CLINICAL INFO — per-category reveal
              =============================================================
              Each category is independently locked, revealed, and audit-
              logged. Each card auto-hides after 60s of being open.
            -->
            <div class="hms-confidential mt-3">
              <div class="hms-confidential-header">
                <i class="mdi mdi-lock-outline me-1"></i>
                <strong>Confidential Clinical Info</strong>
                <span class="hms-muted-sm ms-1">(clinical access only)</span>
              </div>
              <div v-if="!canRevealSensitive" class="hms-confidential-denied mt-1">
                <i class="mdi mdi-shield-alert-outline me-1"></i>Clinical access only
              </div>
              <template v-else>
                <div v-for="c in sensitiveCategories" :key="c.key" class="hms-confidential-card mt-2">
                  <div v-if="!sensitiveOpen[c.key]" class="hms-confidential-locked-sm">
                    <div class="d-flex align-items-center justify-content-between">
                      <div>
                        <span :class="c.icon" class="me-1"></span>
                        <strong>{{ c.label }}</strong>
                      </div>
                      <button class="hms-btn-icon" :title="'Reveal ' + c.label" @click="onRevealCategory(c.key, c.label)"><i class="mdi mdi-eye-outline"></i></button>
                    </div>
                  </div>
                  <div v-else class="hms-confidential-open-sm">
                    <div class="d-flex justify-content-between align-items-start mb-1">
                      <div>
                        <strong><span :class="c.icon" class="me-1"></span>{{ c.label }}</strong>
                        <div class="hms-muted-sm">Visible {{ sensitiveTimers[c.key] }}s · audit-logged</div>
                      </div>
                      <button class="hms-btn-icon" @click="onHideCategory(c.key)" title="Hide"><i class="mdi mdi-eye-off"></i></button>
                    </div>
                    <component :is="'div'" v-html="renderSensitiveBody(c.key)"></component>
                  </div>
                </div>
              </template>
            </div>

            <div class="hms-id-section">
              <div class="hms-id-line">
                <i class="mdi mdi-phone"></i> {{ patient.phone || '—' }}
                <button v-if="patient.phone" class="hms-copy-btn" @click="copyText(patient.phone, 'Phone')" title="Copy phone"><i class="mdi mdi-content-copy"></i></button>
              </div>
              <div class="hms-id-line" v-if="patient.email"><i class="mdi mdi-email-outline"></i> {{ patient.email }}</div>
              <div class="hms-id-line"><i class="mdi mdi-map-marker-outline"></i> {{ [patient.address, patient.city, patient.country].filter(Boolean).join(', ') || '—' }}</div>
              <div class="hms-id-line"><i class="mdi mdi-card-account-details-outline"></i> {{ patient.national_id || '—' }}</div>
            </div>

            <div class="hms-id-section">
              <div class="hms-id-line"><i class="mdi mdi-account-multiple"></i> Next of kin: <strong>{{ patient.next_of_kin_name || '—' }}</strong></div>
              <div class="hms-id-line" v-if="patient.next_of_kin_phone">{{ patient.next_of_kin_phone }} · {{ patient.next_of_kin_relation }}</div>
            </div>

            <div class="hms-id-section" v-if="currentAttending">
              <div class="hms-id-line"><i class="mdi mdi-stethoscope"></i> Attending: <strong>{{ currentAttending.name }}</strong></div>
              <div class="hms-id-line hms-muted-sm">{{ currentAttending.context }}</div>
            </div>

            <div class="hms-id-section">
              <div class="hms-id-line"><i class="mdi mdi-shield-check-outline"></i> {{ patient.insurance_provider || 'No insurance' }}</div>
              <div class="hms-id-line hms-mono" v-if="patient.insurance_number">{{ patient.insurance_number }}</div>
            </div>

            <div class="hms-id-section">
              <div class="hms-muted-sm">
                <i class="mdi mdi-hospital-building"></i> {{ patient.registered_branch || '—' }}<br>
                Registered: {{ formatDate(patient.created_at) }}
              </div>
            </div>

            <!-- Data-sharing consent (per category) -->
            <div class="hms-id-section hms-consent-box">
              <div class="hms-consent-head"><i class="mdi mdi-file-certificate-outline me-1"></i><strong>Data-Sharing Consent</strong></div>
              <div class="hms-muted-sm mb-2">Restrict sharing of sensitive categories (referral / claim / FHIR export). Direct care is unaffected.</div>
              <div v-if="consentLoading" class="hms-muted-sm"><span class="spinner-border spinner-border-sm me-1"></span>Loading…</div>
              <div v-else>
                <div v-for="c in dataConsents" :key="c.category_code" class="hms-consent-row">
                  <span class="hms-consent-name">{{ c.category_name }}</span>
                  <span class="hms-consent-controls">
                    <button class="hms-consent-btn" :class="{on: consentStatus(c)==='granted'}" :disabled="consentSaving===c.category_code" @click="setConsent(c.category_code,'granted')" title="Allow sharing">Allow</button>
                    <button class="hms-consent-btn danger" :class="{on: consentStatus(c)==='restricted'}" :disabled="consentSaving===c.category_code" @click="setConsent(c.category_code,'restricted')" title="Restrict sharing">Restrict</button>
                  </span>
                </div>
                <div v-if="!dataConsents.length" class="hms-muted-sm">No consentable categories.</div>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT — Chart -->
        <div class="col-lg-9">
          <!-- Stat row -->
          <div class="hms-stat-row mb-3">
            <div class="hms-stat"><div class="hms-stat-num">{{ visits.length }}</div><div class="hms-stat-lbl">Visits</div></div>
            <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ admissions.length }}</div><div class="hms-stat-lbl">Admissions</div></div>
            <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ activeIssues }}</div><div class="hms-stat-lbl">Active Issues</div></div>
            <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ prescriptions.length }}</div><div class="hms-stat-lbl">Prescriptions</div></div>
            <div class="hms-stat"><div class="hms-stat-num">{{ labs.length }}</div><div class="hms-stat-lbl">Lab Tests</div></div>
            <div class="hms-stat"><div class="hms-stat-num">{{ radiology.length }}</div><div class="hms-stat-lbl">Imaging</div></div>
            <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">KSh {{ formatNumber(totalUnpaid) }}</div><div class="hms-stat-lbl">Outstanding</div></div>
          </div>

          <!-- Tabs -->
          <div class="hms-tabs mb-3">
            <button class="hms-tab" :class="{ active: tab==='timeline'   }" @click="tab='timeline'"><i class="mdi mdi-timeline-text-outline me-1"></i>Timeline</button>
            <button class="hms-tab" :class="{ active: tab==='vitals'     }" @click="tab='vitals'"><i class="mdi mdi-heart-pulse me-1"></i>Vitals</button>
            <button class="hms-tab" :class="{ active: tab==='visits'     }" @click="tab='visits'"><i class="mdi mdi-history me-1"></i>Visits</button>
            <button class="hms-tab" :class="{ active: tab==='admissions' }" @click="tab='admissions'"><i class="mdi mdi-bed me-1"></i>Admissions</button>
            <button class="hms-tab" :class="{ active: tab==='diagnoses'  }" @click="tab='diagnoses'"><i class="mdi mdi-clipboard-pulse me-1"></i>Diagnoses</button>
            <button class="hms-tab" :class="{ active: tab==='meds'       }" @click="tab='meds'"><i class="mdi mdi-pill me-1"></i>Medications</button>
            <button class="hms-tab" :class="{ active: tab==='invest'     }" @click="tab='invest'"><i class="mdi mdi-flask me-1"></i>Investigations</button>
            <button class="hms-tab" :class="{ active: tab==='billing'    }" @click="tab='billing'"><i class="mdi mdi-cash-multiple me-1"></i>Billing</button>
          </div>

          <!-- TIMELINE -->
          <div v-if="tab === 'timeline'" class="hms-card">
            <div class="hms-section-label d-flex align-items-center justify-content-between">
              <span>UNIFIED TIMELINE ({{ timeline.length }})</span>
              <div class="d-flex gap-1 flex-wrap">
                <button class="hms-chip" v-for="t in timelineTypes" :key="t.code" @click="toggleTimelineType(t.code)" :class="{ active: timelineFilters.has(t.code) }">
                  <i class="mdi" :class="t.icon"></i> {{ t.label }}
                </button>
              </div>
            </div>
            <div v-if="filteredTimeline.length" class="hms-tl">
              <div v-for="(e, i) in filteredTimeline" :key="i" class="hms-tl-item" :class="'tl-' + e.type">
                <div class="hms-tl-icon"><i class="mdi" :class="iconFor(e.type)"></i></div>
                <div class="hms-tl-body">
                  <div class="hms-tl-head">
                    <span class="hms-tl-title">{{ e.title }}</span>
                    <span class="hms-badge" :class="e.badgeClass" v-if="e.badge">{{ e.badge }}</span>
                    <span class="hms-muted-sm ms-auto">{{ formatDateTime(e.at) }} <span class="hms-muted-sm">· {{ relativeTime(e.at) }}</span></span>
                  </div>
                  <div class="hms-tl-detail" v-if="e.detail">{{ e.detail }}</div>
                  <div class="hms-muted-sm" v-if="e.actor"><i class="mdi mdi-account-outline"></i> {{ e.actor }}</div>
                </div>
              </div>
            </div>
            <div v-else class="hms-empty"><i class="mdi mdi-timeline-question-outline"></i>Nothing recorded yet.</div>
          </div>

          <!-- VITALS -->
          <div v-if="tab === 'vitals'" class="hms-card">
            <div v-if="vitalsAsc.length">
              <div class="hms-section-label">VITALS TREND ({{ vitalsAsc.length }} readings)</div>
              <div class="hms-trend-grid">
                <div class="hms-trend" v-for="m in vitalMetrics" :key="m.key">
                  <div class="hms-trend-head">
                    <span class="hms-trend-lbl">{{ m.label }}</span>
                    <span class="hms-trend-now">{{ latestValue(m.key) }} <span class="hms-muted-sm">{{ m.unit }}</span></span>
                  </div>
                  <svg :viewBox="'0 0 200 60'" preserveAspectRatio="none" class="hms-trend-svg">
                    <polyline :points="sparkPoints(m.key, 200, 60)" fill="none" :stroke="m.color" stroke-width="2" />
                  </svg>
                </div>
              </div>

              <div class="hms-section-label mt-4">VITALS LOG</div>
              <div style="overflow-x:auto;">
                <table class="hms-table">
                  <thead><tr><th>When</th><th>BP</th><th>HR</th><th>RR</th><th>T°</th><th>SpO₂</th><th>BS</th><th>NEWS2</th><th>Pain</th><th>Wt</th></tr></thead>
                  <tbody>
                    <tr v-for="v in vitals" :key="v.vital_id">
                      <td class="hms-muted-sm">{{ formatDateTime(v.recorded_at) }}</td>
                      <td>{{ v.bp_systolic }}/{{ v.bp_diastolic }}</td>
                      <td>{{ v.pulse }}</td>
                      <td>{{ v.respiratory_rate }}</td>
                      <td>{{ v.temperature }}</td>
                      <td>{{ v.oxygen_saturation }}</td>
                      <td>{{ v.blood_sugar || '—' }}</td>
                      <td><span class="hms-badge" :class="news2Badge(v.news2_score)">{{ v.news2_score != null ? v.news2_score : '—' }}</span></td>
                      <td>{{ v.pain_score }}/10</td>
                      <td>{{ v.weight || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="hms-empty"><i class="mdi mdi-heart-off-outline"></i>No vitals recorded yet.</div>
          </div>

          <!-- VISITS -->
          <div v-if="tab === 'visits'" class="hms-card">
            <div class="d-flex justify-content-end mb-2">
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="printDoc('visit_history', patient.patient_id)" title="Print full visit & queue history">
                <i class="mdi mdi-printer me-1"></i>Print visit history
              </button>
            </div>
            <div v-if="visits.length" class="hms-list">
              <div v-for="v in visits" :key="v.visit_id" class="hms-rec" @click="openVisit(v)">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <span class="hms-mono">{{ v.visit_number }}</span>
                  <span class="hms-badge hms-badge-info">{{ v.visit_type }}</span>
                  <span v-if="v.triage_level" class="hms-acuity-pill" :class="'acu-' + v.triage_level">{{ acuityShort(v.triage_level) }}</span>
                  <span v-if="v.disposition" class="hms-badge hms-badge-purple">{{ v.disposition }}</span>
                  <span class="hms-muted-sm ms-auto">{{ formatDate(v.visit_date) }}</span>
                </div>
                <div class="hms-rec-title">{{ v.chief_complaint || v.department || '—' }}</div>
                <div class="hms-muted-sm" v-if="v.doctor_name"><i class="mdi mdi-doctor"></i> {{ v.doctor_name }}</div>
              </div>
            </div>
            <div v-else class="hms-empty"><i class="mdi mdi-history"></i>No visits.</div>
          </div>

          <!-- ADMISSIONS -->
          <div v-if="tab === 'admissions'" class="hms-card">
            <div v-if="admissions.length" class="hms-list">
              <div v-for="a in admissions" :key="a.admission_id" class="hms-rec">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <span class="hms-mono">{{ a.admission_number }}</span>
                  <span class="hms-badge hms-badge-info">{{ a.admission_type }}</span>
                  <span class="hms-badge" :class="admStatusBadge(a.status)">{{ a.status }}</span>
                  <span class="hms-muted-sm ms-auto">{{ admLos(a) }}d</span>
                  <button v-if="a.status === 'discharged'" class="hms-btn-icon" @click.stop="printDoc('discharge', a.admission_id)" title="Print discharge summary"><i class="mdi mdi-printer"></i></button>
                </div>
                <div class="hms-rec-title">{{ a.diagnosis_on_admission || a.discharge_diagnosis || '—' }}</div>
                <div class="hms-muted-sm">
                  {{ a.ward_name || '—' }} · Bed {{ a.bed_number || '—' }} ·
                  {{ formatDate(a.admission_date) }}{{ a.actual_discharge_date ? ' → ' + formatDate(a.actual_discharge_date) : ' (active)' }}
                </div>
                <div class="hms-muted-sm" v-if="a.discharge_condition"><i class="mdi mdi-information-outline"></i> Discharged {{ a.discharge_condition }}</div>
              </div>
            </div>
            <div v-else class="hms-empty"><i class="mdi mdi-bed-empty"></i>No admissions.</div>
          </div>

          <!-- DIAGNOSES -->
          <div v-if="tab === 'diagnoses'" class="hms-card">
            <div v-if="diagnoses.length" class="hms-list">
              <div v-for="d in diagnoses" :key="d.diagnosis_id" class="hms-rec">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <span class="hms-mono">{{ d.icd_code || '—' }}</span>
                  <span class="hms-badge hms-badge-info">{{ d.diagnosis_type }}</span>
                  <span class="hms-badge" :class="diagStatusBadge(d.status)">{{ d.status || 'active' }}</span>
                  <span class="hms-muted-sm ms-auto">{{ formatDate(d.diagnosis_date) }}</span>
                </div>
                <div class="hms-rec-title">{{ d.diagnosis_name }}</div>
                <div class="hms-muted-sm" v-if="d.notes">{{ d.notes }}</div>
                <div class="hms-muted-sm" v-if="d.doctor_name"><i class="mdi mdi-doctor"></i> {{ d.doctor_name }}</div>
              </div>
            </div>
            <div v-else class="hms-empty"><i class="mdi mdi-clipboard-pulse-outline"></i>No diagnoses on record.</div>
          </div>

          <!-- MEDICATIONS -->
          <div v-if="tab === 'meds'" class="hms-card">
            <div v-if="prescriptions.length" class="hms-list">
              <div v-for="p in prescriptions" :key="p.prescription_id" class="hms-rec">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <span class="hms-mono">{{ p.prescription_number }}</span>
                  <span class="hms-badge" :class="rxStatusBadge(p.status)">{{ p.status }}</span>
                  <span class="hms-muted-sm">{{ p.item_count }} drug(s)</span>
                  <span class="hms-muted-sm ms-auto">{{ formatDate(p.prescription_date) }}</span>
                  <button class="hms-btn-icon" @click.stop="printDoc('rx', p.prescription_id)" title="Print prescription"><i class="mdi mdi-printer"></i></button>
                </div>
                <div class="hms-rec-title">{{ p.drugs || '—' }}</div>
                <div class="hms-muted-sm" v-if="p.notes">{{ p.notes }}</div>
                <div class="hms-muted-sm" v-if="p.prescriber_name"><i class="mdi mdi-doctor"></i> {{ p.prescriber_name }}</div>
              </div>
            </div>
            <div v-else class="hms-empty"><i class="mdi mdi-pill-off"></i>No prescriptions.</div>
          </div>

          <!-- INVESTIGATIONS -->
          <div v-if="tab === 'invest'" class="hms-card">
            <div class="hms-section-label">LAB ({{ labs.length }})</div>
            <div v-if="labs.length" class="hms-list mb-4">
              <div v-for="l in labs" :key="'lab-' + l.request_id" class="hms-rec">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <span class="hms-mono">{{ l.request_number }}</span>
                  <span class="hms-badge" :class="priorityBadge(l.priority)">{{ l.priority }}</span>
                  <span class="hms-badge hms-badge-info">{{ l.status }}</span>
                  <span class="hms-muted-sm ms-auto">{{ formatDate(l.request_date) }}</span>
                  <button class="hms-btn-icon" @click.stop="printDoc('lab', l.request_id)" title="Print lab report"><i class="mdi mdi-printer"></i></button>
                </div>
                <div class="hms-rec-title">{{ l.tests || '—' }}</div>
                <div class="hms-muted-sm" v-if="l.clinical_notes">{{ l.clinical_notes }}</div>
              </div>
            </div>
            <div v-else class="hms-muted mb-4">No lab requests.</div>

            <div class="hms-section-label">RADIOLOGY ({{ radiology.length }})</div>
            <div v-if="radiology.length" class="hms-list">
              <div v-for="r in radiology" :key="'rad-' + r.radiology_id" class="hms-rec">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <span class="hms-mono">{{ r.request_number }}</span>
                  <span class="hms-badge" :class="priorityBadge(r.priority)">{{ r.priority }}</span>
                  <span class="hms-badge hms-badge-info">{{ r.status }}</span>
                  <span class="hms-muted-sm ms-auto">{{ formatDate(r.created_at) }}</span>
                </div>
                <div class="hms-rec-title">{{ r.scan_type }}{{ r.body_part ? ' · ' + r.body_part : '' }}</div>
              </div>
            </div>
            <div v-else class="hms-muted">No imaging studies.</div>
          </div>

          <!-- BILLING -->
          <div v-if="tab === 'billing'" class="hms-card">
            <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
              <div class="hms-section-label m-0">INVOICES ({{ invoices.length }})</div>
              <button class="hms-btn hms-btn-primary hms-btn-sm" @click="$router.push('/billing?patient_id=' + patient.patient_id)">
                <i class="mdi mdi-receipt-text-plus me-1"></i>New Invoice
              </button>
            </div>

            <!-- Lifetime insurance-savings banner -->
            <div v-if="insuranceSavings && insuranceSavings.savings_total > 0" class="hms-savings-banner mb-3">
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <i class="mdi mdi-shield-check-outline" style="font-size:22px; color:#065f46;"></i>
                <div class="flex-grow-1">
                  <div><strong>Insurance savings on this patient's bills</strong></div>
                  <div class="hms-muted-sm">
                    Catalogue total <strong>KSh {{ formatNumber(insuranceSavings.catalogue_total) }}</strong> · Billed
                    <strong>KSh {{ formatNumber(insuranceSavings.billed_total) }}</strong> · <strong class="hms-text-success">{{ insuranceSavings.rate_card_lines }} line{{ insuranceSavings.rate_card_lines !== 1 ? 's' : '' }}</strong>
                    across <strong>{{ insuranceSavings.invoices_with_savings }} invoice{{ insuranceSavings.invoices_with_savings !== 1 ? 's' : '' }}</strong>
                    <span v-if="insuranceSavings.providers && insuranceSavings.providers.length"> · providers: {{ insuranceSavings.providers.join(', ') }}</span>
                  </div>
                </div>
                <div class="hms-savings-amt">KSh {{ formatNumber(insuranceSavings.savings_total) }}</div>
              </div>
            </div>

            <div v-if="invoices.length" style="overflow-x:auto;">
              <table class="hms-table">
                <thead><tr><th>Invoice #</th><th>Date</th><th>Total</th><th>Paid</th><th>Balance</th><th>Insurance saved</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  <tr v-for="inv in invoices" :key="inv.invoice_id">
                    <td class="hms-mono">{{ inv.invoice_number }}</td>
                    <td>{{ formatDate(inv.invoice_date) }}</td>
                    <td>KSh {{ formatNumber(inv.total_amount) }}</td>
                    <td>KSh {{ formatNumber(inv.paid_amount) }}</td>
                    <td><strong :class="parseFloat(inv.balance) > 0 ? 'hms-text-danger' : ''">KSh {{ formatNumber(inv.balance) }}</strong></td>
                    <td>
                      <template v-if="Number(inv.savings_total) > 0">
                        <span class="hms-rate-pill" :title="'Rate card(s) applied: ' + (inv.rate_card_providers || '')">
                          <i class="mdi mdi-shield-check-outline me-1"></i>KSh {{ formatNumber(inv.savings_total) }}
                        </span>
                        <div class="hms-muted-sm">{{ inv.rate_card_lines }} line<span v-if="inv.rate_card_lines !== 1">s</span> · {{ inv.rate_card_providers || '—' }}</div>
                      </template>
                      <span v-else class="hms-muted-sm">—</span>
                    </td>
                    <td><span class="hms-badge" :class="invoiceBadge(inv.payment_status)">{{ inv.payment_status }}</span></td>
                    <td>
                      <div class="d-flex gap-1 flex-wrap">
                        <button v-if="parseFloat(inv.balance) > 0" class="hms-btn-icon hms-btn-icon-green" :disabled="payLinkBusy[inv.invoice_id]" @click="doSendPayLink(inv)" title="Send pay link (SMS + email)">
                          <i class="mdi" :class="payLinkBusy[inv.invoice_id] ? 'mdi-loading mdi-spin' : 'mdi-share-variant'"></i>
                        </button>
                        <a v-if="inv.pay_token && parseFloat(inv.balance) > 0" class="hms-btn-icon" :href="payLinkUrl(inv.pay_token)" target="_blank" title="Open public pay page">
                          <i class="mdi mdi-link-variant"></i>
                        </a>
                        <button class="hms-btn-icon" @click="printDoc('invoice', inv.invoice_id)" title="Print invoice"><i class="mdi mdi-printer"></i></button>
                        <button class="hms-btn-icon" @click="$router.push('/billing?open_invoice=' + inv.invoice_id)" title="Open in billing (record payment / STK)"><i class="mdi mdi-open-in-new"></i></button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="hms-empty"><i class="mdi mdi-receipt-text-outline"></i>No invoices for this patient yet. <button class="hms-btn-link" @click="$router.push('/billing?patient_id=' + patient.patient_id)">Create one →</button></div>
          </div>

          <!-- ============ DANGER ZONE (tucked at the bottom) ============ -->
          <div class="hms-danger-zone mt-4" v-if="patient">
            <button class="hms-danger-toggle" @click="dangerOpen = !dangerOpen">
              <i class="mdi" :class="dangerOpen ? 'mdi-chevron-down' : 'mdi-chevron-right'"></i>
              Advanced — danger zone
            </button>
            <div v-if="dangerOpen" class="hms-danger-body">
              <div class="hms-danger-title">Soft-delete patient record</div>
              <div class="hms-danger-desc">
                Marks this patient as deleted. They will stop appearing in patient lists, queues, and reports.
                Their clinical history is retained for compliance but no longer surfaced. This action is reversible
                by a database admin only.
              </div>
              <button class="hms-btn hms-btn-red" :disabled="deleting" @click="confirmDelete">
                <i class="mdi" :class="deleting ? 'mdi-loading mdi-spin' : 'mdi-trash-can-outline'"></i>
                {{ deleting ? ' Deleting…' : ' Soft-delete this patient' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ EDIT PATIENT MODAL (inline on the chart) ============ -->
      <div class="hms-modal-overlay" v-if="editOpen" @click.self="closeEdit">
        <div class="hms-modal" style="max-width:780px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-account-edit-outline me-2"></i>Edit Patient</span>
            <button class="hms-modal-close" @click="closeEdit" :disabled="editSaving">&times;</button>
          </div>

          <div v-if="editError" class="hms-alert hms-alert-danger mb-2"><i class="mdi mdi-alert-circle me-1"></i>{{ editError }}</div>

          <div class="hms-section-label mt-1">BASIC INFORMATION</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">First Name *</label>
              <input class="hms-input" v-model="editForm.first_name" />
            </div>
            <div class="col-md-4"><label class="hms-label">Last Name *</label>
              <input class="hms-input" v-model="editForm.last_name" />
            </div>
            <div class="col-md-4"><label class="hms-label">Other Names</label>
              <input class="hms-input" v-model="editForm.other_names" placeholder="Optional" />
            </div>
            <div class="col-md-4"><label class="hms-label">Date of Birth</label>
              <input type="date" class="hms-input" v-model="editForm.dob" />
            </div>
            <div class="col-md-2"><label class="hms-label">Age (yrs)</label>
              <input type="number" class="hms-input" :value="ageFromDob(editForm.dob)" @input="onAgeInput" min="0" max="130" placeholder="—" />
            </div>
            <div class="col-md-3"><label class="hms-label">Gender</label>
              <select class="hms-input" v-model="editForm.gender">
                <option value="">Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-md-3"><label class="hms-label">Blood Group</label>
              <select class="hms-input" v-model="editForm.blood_group">
                <option v-for="bg in bloodGroups" :key="bg" :value="bg">{{ bg }}</option>
              </select>
            </div>
          </div>

          <div class="hms-section-label">CONTACT</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Phone</label>
              <input class="hms-input" v-model="editForm.phone" placeholder="07XX XXX XXX" />
            </div>
            <div class="col-md-4"><label class="hms-label">Email</label>
              <input class="hms-input" v-model="editForm.email" type="email" />
            </div>
            <div class="col-md-4"><label class="hms-label">National ID</label>
              <input class="hms-input" v-model="editForm.national_id" />
            </div>
            <div class="col-md-12">
              <label class="hms-label">Address &amp; Location</label>
              <div class="d-flex gap-2 flex-wrap">
                <input class="hms-input" style="flex:1; min-width:240px;" v-model="addressQuery"
                       @keydown.enter.prevent="geocodeAddress(false)"
                       @input="onAddressInput"
                       placeholder="Search address — town, estate, landmark…" />
                <button type="button" class="hms-btn hms-btn-blue" @click="geocodeAddress(false)" :disabled="addressBusy">
                  <i class="mdi" :class="addressBusy ? 'mdi-loading mdi-spin' : 'mdi-magnify'"></i> Search
                </button>
                <button type="button" class="hms-btn hms-btn-blue" @click="useMyLocation" :disabled="addressBusy" title="Use current GPS">
                  <i class="mdi mdi-crosshairs-gps"></i> GPS
                </button>
              </div>
              <div v-if="addressSuggest.length" class="hms-suggest mt-1">
                <div v-for="(s, i) in addressSuggest" :key="i" class="hms-suggest-item" @click="pickAddressSuggestion(s)">
                  <i class="mdi mdi-map-marker-outline me-1"></i>{{ s.display_name }}
                </div>
              </div>
              <div ref="editAddressMap" class="hms-leaflet-map mt-2"></div>
              <div class="hms-muted-sm mt-1">
                <span v-if="editForm.latitude != null && editForm.longitude != null">
                  <i class="mdi mdi-crosshairs-gps me-1"></i>
                  Pin: {{ Number(editForm.latitude).toFixed(5) }}, {{ Number(editForm.longitude).toFixed(5) }}
                </span>
                <span v-else>Drag the pin or click the map to set GPS.</span>
              </div>
              <input class="hms-input mt-2" v-model="editForm.address" placeholder="Refine address text" />
            </div>
            <div class="col-md-4"><label class="hms-label">Country</label>
              <input class="hms-input" list="hms-chart-countries" v-model="editForm.country" placeholder="Type to search…" />
              <datalist id="hms-chart-countries">
                <option v-for="c in countries" :key="c" :value="c"></option>
              </datalist>
            </div>
          </div>

          <div class="hms-section-label">MEDICAL</div>
          <div class="row g-3 mb-3">
            <div class="col-md-8"><label class="hms-label">Allergies</label>
              <div class="hms-chip-input">
                <span v-for="(t, i) in allergyTags" :key="i" class="hms-chip">
                  {{ t }}
                  <button type="button" class="hms-chip-x" @click="removeAllergy(i)" title="Remove">&times;</button>
                </span>
                <input class="hms-chip-text" list="hms-chart-allergies"
                       v-model="allergyInput"
                       @keydown.enter.prevent="addAllergyFromInput"
                       @keydown.,.prevent="addAllergyFromInput"
                       @keydown.tab="addAllergyFromInput"
                       @change="addAllergyFromInput"
                       placeholder="Type or pick — press Enter to add another…" />
              </div>
              <datalist id="hms-chart-allergies">
                <option v-for="a in commonAllergies" :key="a" :value="a"></option>
              </datalist>
            </div>
            <div class="col-md-4"><label class="hms-label">Chronic conditions</label>
              <input class="hms-input" v-model="editForm.chronic_conditions" placeholder="e.g. Diabetes, Hypertension" />
            </div>
          </div>

          <!-- REGISTRATION SOURCE + CONDITIONAL REFERRAL BLOCK -->
          <div class="hms-section-label">REGISTRATION SOURCE</div>
          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Source</label>
              <select class="hms-input" v-model="editForm.registration_source">
                <option value="walk_in">Walk-in</option>
                <option value="referral">Referral (from another facility)</option>
                <option value="emergency">Emergency</option>
                <option value="online">Online</option>
              </select>
            </div>
          </div>

          <div v-if="editForm.registration_source === 'referral'">
            <div class="hms-alert hms-alert-amber mb-2">
              <i class="mdi mdi-alert-circle-outline me-1"></i>
              <strong>Referral traceability</strong> — used if the referring facility delayed unsafely.
            </div>

            <div class="hms-section-label">REFERRING FACILITY</div>
            <div class="row g-3 mb-3">
              <div class="col-md-6"><label class="hms-label">Facility Name</label>
                <input class="hms-input" v-model="editForm.referral_source_facility" placeholder="e.g. Karen Hospital" />
              </div>
              <div class="col-md-3"><label class="hms-label">Facility Type</label>
                <select class="hms-input" v-model="editForm.referral_source_type">
                  <option value="">—</option>
                  <option value="public">Public (MoH)</option>
                  <option value="private">Private</option>
                  <option value="mission">Mission / Faith-based</option>
                  <option value="ngo">NGO</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
              <div class="col-md-3"><label class="hms-label">KEPH Level</label>
                <select class="hms-input" v-model="editForm.referral_source_level">
                  <option value="">—</option>
                  <option value="L1">L1 — Community Unit</option>
                  <option value="L2">L2 — Dispensary</option>
                  <option value="L3">L3 — Health Centre</option>
                  <option value="L4">L4 — Sub-County</option>
                  <option value="L5">L5 — County Referral</option>
                  <option value="L6">L6 — National Referral</option>
                  <option value="private_clinic">Private clinic</option>
                  <option value="private_hospital">Private hospital</option>
                </select>
              </div>
              <div class="col-md-6"><label class="hms-label">County</label>
                <input class="hms-input" v-model="editForm.referral_source_county" placeholder="e.g. Narok" />
              </div>
              <div class="col-md-3"><label class="hms-label">Mode of Transport</label>
                <select class="hms-input" v-model="editForm.referral_source_transport">
                  <option value="">—</option>
                  <option value="ambulance">Ambulance</option>
                  <option value="private_car">Private car</option>
                  <option value="public_transport">Public transport</option>
                  <option value="walked">Walked</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div class="col-md-3"><label class="hms-check mt-4">
                <input type="checkbox" v-model="editForm.referral_source_documentation" :true-value="1" :false-value="0" />
                Patient brought referral letter
              </label></div>
            </div>

            <div class="hms-section-label">REFERRING CLINICIAN</div>
            <div class="row g-3 mb-3">
              <div class="col-md-6"><label class="hms-label">Contact Name</label>
                <input class="hms-input" v-model="editForm.referral_source_contact_name" />
              </div>
              <div class="col-md-6"><label class="hms-label">Contact Phone</label>
                <input class="hms-input" v-model="editForm.referral_source_contact_phone" placeholder="07XX XXX XXX" />
              </div>
            </div>

            <div class="hms-section-label">CLINICAL HAND-OVER</div>
            <div class="row g-3 mb-3">
              <div class="col-md-6"><label class="hms-label">Reason for Referral</label>
                <textarea class="hms-input" rows="2" v-model="editForm.referral_source_reason"></textarea>
              </div>
              <div class="col-md-3"><label class="hms-label">Arrival Condition</label>
                <select class="hms-input" v-model="editForm.referral_arrival_condition">
                  <option value="">—</option>
                  <option value="stable">Stable</option>
                  <option value="critical">Critical</option>
                  <option value="moribund">Moribund</option>
                  <option value="dead_on_arrival">Dead on arrival</option>
                </select>
              </div>
              <div class="col-md-3"><label class="hms-label">Date/Time Referred</label>
                <input type="datetime-local" class="hms-input" v-model="editForm.referral_source_date" />
              </div>
              <div class="col-12"><label class="hms-label">Hand-over Notes</label>
                <textarea class="hms-input" rows="2" v-model="editForm.referral_source_notes"></textarea>
              </div>
            </div>
          </div>

          <div class="hms-section-label">NEXT OF KIN</div>
          <div class="row g-3 mb-3">
            <div class="col-md-5"><label class="hms-label">Name</label>
              <input class="hms-input" v-model="editForm.next_of_kin_name" />
            </div>
            <div class="col-md-4"><label class="hms-label">Phone</label>
              <input class="hms-input" v-model="editForm.next_of_kin_phone" placeholder="07XX XXX XXX" />
            </div>
            <div class="col-md-3"><label class="hms-label">Relationship</label>
              <input class="hms-input" v-model="editForm.next_of_kin_relation" placeholder="e.g. Spouse, Parent" />
            </div>
          </div>

          <div class="hms-section-label">INSURANCE</div>
          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Provider</label>
              <select class="hms-input" v-model="editForm.insurance_provider">
                <option value="">— None / Cash —</option>
                <option v-for="p in insuranceProviders" :key="p" :value="p">{{ p }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Policy / Member No</label>
              <input class="hms-input" v-model="editForm.insurance_number" />
            </div>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-blue flex-fill" @click="closeEdit" :disabled="editSaving">Cancel</button>
            <button class="hms-btn hms-btn-primary flex-fill" @click="saveEdit" :disabled="editSaving">
              <i class="mdi" :class="editSaving ? 'mdi-loading mdi-spin' : 'mdi-content-save'"></i>
              {{ editSaving ? ' Saving…' : ' Save Changes' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete confirm modal -->
      <div class="hms-modal-overlay" v-if="confirmingDelete" @click.self="confirmingDelete=false">
        <div class="hms-modal" style="max-width:440px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-alert-octagon me-2 hms-text-danger"></i>Confirm delete</span>
            <button class="hms-modal-close" @click="confirmingDelete=false">&times;</button>
          </div>
          <p>This will soft-delete <strong>{{ patient.first_name }} {{ patient.last_name }}</strong> (<span class="hms-mono">{{ patient.patient_no }}</span>).</p>
          <p>Type the patient number below to confirm:</p>
          <input class="hms-input" v-model="deleteConfirmText" :placeholder="patient.patient_no" autofocus />
          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-blue flex-fill" @click="confirmingDelete=false">Cancel</button>
            <button class="hms-btn hms-btn-red flex-fill" :disabled="deleteConfirmText !== patient.patient_no || deleting" @click="doDelete">
              <i class="mdi" :class="deleting ? 'mdi-loading mdi-spin' : 'mdi-trash-can'"></i>
              {{ deleting ? ' Deleting…' : ' Delete' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>

    <!-- =============================================================
         VISIT DETAIL MODAL — full visit info, gates sensitive diagnoses
         ============================================================= -->
    <div v-if="showVisitDetail" class="hms-modal-overlay" @click.self="closeVisitDetail">
      <div class="hms-modal" style="max-width: 920px; width:95%;">
        <div class="hms-modal-title">
          <span v-if="visitDetail">
            <i class="mdi mdi-history me-1"></i>
            Visit {{ visitDetail.visit && visitDetail.visit.visit_number }}
            <span class="hms-muted-sm ms-2">{{ visitDetail.visit && visitDetail.visit.visit_date }}</span>
          </span>
          <span v-else><i class="mdi mdi-loading mdi-spin"></i> Loading visit…</span>
          <button class="hms-modal-close" @click="closeVisitDetail">&times;</button>
        </div>
        <div v-if="visitDetailLoading" class="hms-empty"><i class="mdi mdi-loading mdi-spin"></i> Loading…</div>
        <div v-if="visitDetail">
          <!-- visit metadata -->
          <div class="hms-card mb-3">
            <div class="row g-2">
              <div class="col-md-4"><div class="hms-kv-key">Type</div><div>{{ visitDetail.visit.visit_type }}</div></div>
              <div class="col-md-4"><div class="hms-kv-key">Department</div><div>{{ visitDetail.visit.department }}</div></div>
              <div class="col-md-4"><div class="hms-kv-key">Doctor</div><div>Dr {{ (visitDetail.visit.doctor_first || '') + ' ' + (visitDetail.visit.doctor_last || '') || '—' }}</div></div>
              <div class="col-12"><div class="hms-kv-key">Chief complaint</div><div>{{ visitDetail.visit.chief_complaint || '—' }}</div></div>
              <div class="col-md-4"><div class="hms-kv-key">Triage</div><div>{{ visitDetail.visit.triage_level || '—' }}</div></div>
              <div class="col-md-4"><div class="hms-kv-key">Status</div><div>{{ visitDetail.visit.visit_status }}</div></div>
              <div class="col-md-4"><div class="hms-kv-key">Disposition</div><div>{{ visitDetail.visit.disposition || '—' }}</div></div>
            </div>
          </div>

          <!-- Vitals -->
          <div v-if="visitDetail.vitals && visitDetail.vitals.length" class="hms-card mb-3">
            <div class="hms-section-label">VITALS</div>
            <table class="hms-table">
              <thead><tr><th>BP</th><th>Pulse</th><th>RR</th><th>SpO₂</th><th>Temp</th><th>Wt</th><th>NEWS2</th><th>Notes</th></tr></thead>
              <tbody>
                <tr v-for="v in visitDetail.vitals" :key="v.vital_id">
                  <td>{{ v.bp_systolic }}/{{ v.bp_diastolic }}</td>
                  <td>{{ v.pulse }}</td>
                  <td>{{ v.respiratory_rate }}</td>
                  <td>{{ v.oxygen_saturation }}%</td>
                  <td>{{ v.temperature }}°C</td>
                  <td>{{ v.weight }} kg</td>
                  <td><span :class="{ 'hms-warn-strip': v.news2_score >= 5, 'hms-info-strip': v.news2_score < 5 }">{{ v.news2_score }}</span></td>
                  <td><span class="hms-muted-sm">{{ v.notes }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Diagnoses -->
          <div v-if="visitDetail.diagnoses && visitDetail.diagnoses.length" class="hms-card mb-3">
            <div class="hms-section-label">DIAGNOSES</div>
            <div v-for="d in visitDetail.diagnoses" :key="d.diagnosis_id" class="hms-diag-row">
              <template v-if="d.sensitive_category && !visitRevealedSensitive[d.diagnosis_id] && canRevealSensitive">
                <span class="hms-mono">— · —</span>
                <span class="hms-warn-strip" style="display:inline-block; padding:2px 8px; font-size:11px;">
                  <i class="mdi mdi-lock-outline me-1"></i>{{ d.sensitive_category }} — protected
                </span>
                <button class="hms-btn hms-btn-warning hms-btn-sm ms-2" @click="revealSensitiveDiagnosis(d)"><i class="mdi mdi-eye-outline"></i> Reveal</button>
              </template>
              <template v-else-if="d.sensitive_category && !canRevealSensitive">
                <span class="hms-mono">— · —</span>
                <span class="hms-muted-sm"><i class="mdi mdi-shield-alert-outline me-1"></i>Clinical access only</span>
              </template>
              <template v-else>
                <span class="hms-mono">{{ d.icd_code || '—' }}</span>
                <strong class="ms-1">{{ d.diagnosis_name }}</strong>
                <span class="hms-badge hms-badge-info ms-2">{{ d.diagnosis_type }}</span>
                <span v-if="d.notes" class="hms-muted-sm ms-2">— {{ d.notes }}</span>
              </template>
            </div>
          </div>

          <!-- Lab requests — sensitive tests gated per row -->
          <div v-if="visitDetail.lab_requests && visitDetail.lab_requests.length" class="hms-card mb-3">
            <div class="hms-section-label">LAB ORDERS &amp; RESULTS</div>
            <div v-for="l in visitDetail.lab_requests" :key="l.request_id" style="border-left:3px solid #0EA5E9; padding:8px 12px; margin-bottom:10px; background:#f7f8fb;">
              <div class="d-flex align-items-center flex-wrap gap-2">
                <span class="hms-mono fw-semibold">{{ l.request_number || ('REQ-' + l.request_id) }}</span>
                <span class="hms-muted-sm">{{ l.clinical_notes }}</span>
                <span class="hms-badge ms-2" :class="l.priority === 'urgent' ? 'hms-badge-amber' : 'hms-badge-neutral'">{{ l.priority }}</span>
                <span class="hms-badge ms-2" :class="l.status === 'completed' ? 'hms-badge-success' : 'hms-badge-info'">{{ l.status }}</span>
              </div>

              <!-- All-protected request: single locked card with one Reveal -->
              <template v-if="l.has_sensitive_item && !visitRevealedSensitive['lab_' + l.request_id]">
                <div class="hms-confidential-locked-sm mt-2">
                  <i class="mdi mdi-lock-outline me-1"></i>
                  <strong>This order contains protected tests</strong>
                  <span class="hms-muted-sm ms-1">({{ (l.sensitive_categories || []).join(', ') }})</span>
                  <button v-if="canRevealSensitive" class="hms-btn hms-btn-warning hms-btn-sm ms-2" @click="revealSensitiveLab(l)">
                    <i class="mdi mdi-eye-outline"></i> Reveal (audit-logged)
                  </button>
                  <span v-else class="hms-muted-sm ms-2">Clinical access only</span>
                  <!-- Non-sensitive items in the same order still show -->
                  <div class="mt-2" v-if="l.items && l.items.length">
                    <div v-for="it in l.items" :key="it.item_id">
                      <template v-if="!it.sensitive_category">
                        <span class="hms-mono">{{ it.test_name }}</span>
                        <span class="ms-2"><strong>{{ it.result_value || '—' }}</strong> {{ it.result_unit }}</span>
                        <span class="hms-muted-sm ms-2" v-if="it.normal_range">(ref {{ it.normal_range }})</span>
                        <span v-if="it.result_flag && it.result_flag !== 'normal'" class="hms-badge ms-2" :class="(it.result_flag.indexOf('critical') === 0) ? 'hms-badge-danger' : 'hms-badge-amber'">{{ it.result_flag }}</span>
                      </template>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Fully revealed: every item shows -->
              <div v-else-if="l.items && l.items.length" class="mt-2">
                <table class="hms-table" style="font-size: 11px;">
                  <thead><tr><th>Test</th><th>Result</th><th>Unit</th><th>Normal</th><th>Flag</th></tr></thead>
                  <tbody>
                    <tr v-for="it in l.items" :key="it.item_id">
                      <td>
                        <span class="hms-mono">{{ it.test_name }}</span>
                        <span v-if="it.sensitive_category" class="hms-badge ms-1" style="font-size:9px; background:#fde68a; color:#78350f;"><i class="mdi mdi-lock-open-outline"></i> {{ it.sensitive_category }}</span>
                      </td>
                      <td><strong>{{ it.result_value || '—' }}</strong></td>
                      <td>{{ it.result_unit }}</td>
                      <td><span class="hms-muted-sm">{{ it.normal_range }}</span></td>
                      <td><span v-if="it.result_flag" class="hms-badge" :class="(it.result_flag.indexOf('critical') === 0) ? 'hms-badge-danger' : (it.result_flag === 'normal' ? 'hms-badge-success' : 'hms-badge-amber')">{{ it.result_flag }}</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Imaging — sensitive orders gated per row -->
          <div v-if="visitDetail.imaging_requests && visitDetail.imaging_requests.length" class="hms-card mb-3">
            <div class="hms-section-label">IMAGING</div>
            <div v-for="r in visitDetail.imaging_requests" :key="r.radiology_id" style="border-left:3px solid #7C3AED; padding:8px 12px; margin-bottom:10px; background:#f7f8fb;">
              <div class="d-flex align-items-center flex-wrap gap-2">
                <strong>{{ r.scan_type }}</strong>
                <span v-if="r.body_part" class="ms-1">— {{ r.body_part }}</span>
                <span v-if="r.sensitive_category" class="hms-badge ms-2" style="background:#fde68a; color:#78350f;"><i class="mdi" :class="visitRevealedSensitive['rad_' + r.radiology_id] ? 'mdi-lock-open-outline' : 'mdi-lock-outline'"></i> {{ r.sensitive_category }}</span>
                <span class="hms-badge ms-auto" :class="r.status === 'reported' ? 'hms-badge-success' : 'hms-badge-info'">{{ r.status }}</span>
              </div>

              <template v-if="r.sensitive_category && !visitRevealedSensitive['rad_' + r.radiology_id]">
                <div class="hms-confidential-locked-sm mt-2">
                  <i class="mdi mdi-lock-outline me-1"></i>
                  <strong>Indication + report contain protected information</strong>
                  <button v-if="canRevealSensitive" class="hms-btn hms-btn-warning hms-btn-sm ms-2" @click="revealSensitiveImaging(r)">
                    <i class="mdi mdi-eye-outline"></i> Reveal (audit-logged)
                  </button>
                  <span v-else class="hms-muted-sm ms-2">Clinical access only</span>
                </div>
              </template>
              <template v-else>
                <div v-if="r.clinical_indication" class="mt-1"><strong>Indication:</strong> {{ r.clinical_indication }}</div>
                <div v-if="r.report" class="mt-1 hms-info-strip"><strong>Report:</strong> {{ r.report }}</div>
                <div class="hms-muted-sm mt-1" v-if="r.scheduled_date">Scheduled: {{ r.scheduled_date }}<span v-if="r.performed_date"> · Performed: {{ r.performed_date }}</span></div>
              </template>
            </div>
          </div>

          <!-- Prescriptions -->
          <div v-if="visitDetail.prescription_items && visitDetail.prescription_items.length" class="hms-card mb-3">
            <div class="hms-section-label">PRESCRIPTIONS</div>
            <div v-for="pi in visitDetail.prescription_items" :key="pi.item_id" class="hms-diag-row">
              <strong>{{ pi.drug_name }}</strong>
              <span class="ms-2">{{ pi.dosage }} · {{ pi.frequency }} · {{ pi.duration }}</span>
              <span class="hms-muted-sm ms-2" v-if="pi.notes">— {{ pi.notes }}</span>
              <span class="hms-badge ms-2" :class="pi.status === 'dispensed' ? 'hms-badge-success' : 'hms-badge-info'">{{ pi.status }}</span>
            </div>
          </div>

          <!-- SOAP notes — gated when they contain protected language -->
          <div v-if="visitDetail.consultation_notes && visitDetail.consultation_notes.length" class="hms-card mb-3">
            <div class="hms-section-label">CLINICAL NOTES</div>
            <div v-for="n in visitDetail.consultation_notes" :key="n.note_id" style="border-left:3px solid #0047AB; padding:6px 12px; margin-bottom:10px; background:#f7f8fb;">
              <template v-if="noteIsSensitive(n) && !visitRevealedSensitive['note_' + n.note_id]">
                <div class="hms-confidential-locked-sm">
                  <i class="mdi mdi-lock-outline me-1"></i>
                  <strong>Clinical note contains protected information.</strong>
                  <button v-if="canRevealSensitive" class="hms-btn hms-btn-warning hms-btn-sm ms-2" @click="revealSensitiveNote(n)"><i class="mdi mdi-eye-outline"></i> Reveal (audit-logged)</button>
                  <span v-else class="hms-muted-sm ms-2">Clinical access only</span>
                </div>
              </template>
              <template v-else>
                <div v-if="n.subjective"><strong>S:</strong> {{ n.subjective }}</div>
                <div v-if="n.objective"><strong>O:</strong> {{ n.objective }}</div>
                <div v-if="n.assessment"><strong>A:</strong> {{ n.assessment }}</div>
                <div v-if="n.plan"><strong>P:</strong> {{ n.plan }}</div>
              </template>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button v-if="visitDetail && visitDetail.visit && visitDetail.visit.visit_status !== 'completed'" class="hms-btn hms-btn-primary" @click="visitGoConsultation(visitDetail.visit.visit_id)">
            <i class="mdi mdi-doctor me-1"></i>Continue in Consultation
          </button>
          <button class="hms-btn hms-btn-neutral" @click="closeVisitDetail">Close</button>
        </div>
      </div>
    </div>

    <!-- ============================================================
         REVEAL-REASON MODAL — replaces window.prompt()
         ============================================================ -->
    <div v-if="revealPrompt.open" class="hms-modal-overlay" @click.self="cancelReveal" @keydown.esc="cancelReveal">
      <div class="hms-modal" style="max-width: 520px; width:95%;">
        <div class="hms-modal-title">
          <span>
            <i class="mdi mdi-shield-lock-open-outline me-1" style="color:#b45309;"></i>
            Reveal {{ revealPrompt.label }}
          </span>
          <button class="hms-modal-close" @click="cancelReveal">&times;</button>
        </div>

        <div class="hms-reveal-banner">
          <i class="mdi mdi-information-outline me-1"></i>
          You are about to access <strong>protected clinical information</strong>. This access will be
          permanently recorded in the audit log with your username, the patient ID, the reason below,
          and the time of access.
        </div>

        <label class="hms-label mt-3">Reason for access <span class="hms-text-danger">*</span></label>
        <textarea
          ref="revealReasonInput"
          v-model="revealPrompt.reason"
          class="hms-input"
          rows="3"
          placeholder="e.g. Clinical review before treatment, medication reconciliation, second-opinion …"
          @keydown.ctrl.enter="confirmReveal"
          @keydown.meta.enter="confirmReveal"></textarea>
        <div class="hms-muted-sm mt-1">Press <kbd>⌘ Enter</kbd> to submit · <kbd>Esc</kbd> to cancel</div>

        <div class="d-flex justify-content-end gap-2 mt-3">
          <button class="hms-btn hms-btn-neutral" @click="cancelReveal">Cancel</button>
          <button class="hms-btn hms-btn-warning" @click="confirmReveal" :disabled="!revealPrompt.reason || !revealPrompt.reason.trim()">
            <i class="mdi mdi-eye-outline me-1"></i>Reveal &amp; log access
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
  --acu-1:#dc3545; --acu-2:#fd7e14; --acu-3:#ffc107; --acu-4:#28a745; --acu-5:#1a7fe8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
  --acu-1:#dc3545; --acu-2:#fd7e14; --acu-3:#ffc107; --acu-4:#28a745; --acu-5:#0047AB;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-consent-box { border-top: 1px solid var(--hms-br); }
.hms-consent-head { font-size: 13px; color: var(--hms-h); margin-bottom: 4px; }
.hms-consent-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 4px 0; border-bottom: 1px dashed var(--hms-br); }
.hms-consent-row:last-child { border-bottom: none; }
.hms-consent-name { font-size: 12px; color: var(--hms-h); }
.hms-consent-controls { display: inline-flex; gap: 4px; flex-shrink: 0; }
.hms-consent-btn { border: 1px solid var(--hms-input-br); background: var(--hms-bg4); color: var(--hms-s); border-radius: 6px; padding: 2px 8px; font-size: 11px; font-weight: 600; cursor: pointer; }
.hms-consent-btn.on { background: var(--hms-success); color: #fff; border-color: var(--hms-success); }
.hms-consent-btn.danger.on { background: var(--hms-danger); color: #fff; border-color: var(--hms-danger); }
.hms-consent-btn:disabled { opacity: .5; cursor: not-allowed; }
.hms-copyable { display: inline-flex; align-items: center; gap: 6px; }
.hms-copy-btn { background: transparent; border: none; color: var(--hms-s); cursor: pointer; padding: 2px 5px; margin-left: 2px; border-radius: 5px; font-size: 13px; line-height: 1; opacity: 0.55; transition: all .15s; vertical-align: middle; }
.hms-copy-btn:hover { opacity: 1; color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding: 20px 24px 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

/* IDENTITY */
.hms-identity { text-align: center; }
.hms-avatar-xl { width: 96px; height: 96px; border-radius: 50%; background: var(--hms-primary-lt); color: var(--hms-primary); display: flex; align-items: center; justify-content: center; font-size: 36px; font-weight: 800; margin: 0 auto 12px; }
.hms-id-name { color: var(--hms-h); font-size: 18px; font-weight: 700; }
.hms-id-row { display: flex; gap: 6px; flex-wrap: wrap; justify-content: center; }
.hms-id-section { margin-top: 16px; padding-top: 14px; border-top: 1px solid var(--hms-br); text-align: left; }
.hms-id-line { padding: 3px 0; font-size: 13px; color: var(--hms-h); }
.hms-id-line i { color: var(--hms-primary); width: 18px; margin-right: 4px; }

.hms-warn-strip { background: rgba(220,53,69,.08); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.25); border-radius: 6px; padding: 6px 10px; font-size: 12px; text-align: left; }
.hms-warn-red { background: rgba(220,53,69,.18); color: #b91c1c; border-color: rgba(220,53,69,.4); font-weight: 600; }
.hms-info-strip { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 6px; padding: 6px 10px; font-size: 12px; text-align: left; }

/* CONFIDENTIAL panel (sensitive-info reveal guard) */
.hms-confidential-header { font-size: 12px; color: #78350f; background: linear-gradient(135deg, #fef3c7, #fde68a); border: 1px dashed #d97706; border-radius: 8px 8px 0 0; padding: 6px 10px; }
.hms-confidential-card { background: #fff; border: 1px solid #fde68a; border-radius: 6px; }
.hms-confidential-locked-sm { padding: 6px 10px; font-size: 12px; color: #78350f; }
.hms-reveal-banner { background: linear-gradient(135deg, #fef3c7, #fde68a); border: 1px solid #d97706; color: #78350f; border-radius: 8px; padding: 10px 14px; font-size: 12.5px; line-height: 1.45; }
.hms-savings-banner { background: linear-gradient(135deg, rgba(16,185,129,.08), rgba(16,185,129,.16)); border: 1px solid rgba(16,185,129,.3); border-radius: 8px; padding: 12px 14px; color: #065f46; font-size: 13px; }
.hms-savings-amt { font-size: 20px; font-weight: 800; color: #065f46; }
.hms-rate-pill { display: inline-block; padding: 2px 8px; background: rgba(124,58,237,.10); color: #6d28d9; border: 1px solid rgba(124,58,237,.30); border-radius: 12px; font-size: 11px; font-weight: 600; }
kbd { background: #1f2937; color: #fff; padding: 1px 6px; border-radius: 4px; font-size: 10px; font-family: ui-monospace, Menlo, monospace; }
.hms-confidential-open-sm { padding: 8px 10px; font-size: 12px; background: rgba(220,53,69,.05); border-left: 4px solid #dc2626; }
.hms-confidential-title-open { font-weight: 700; color: #b91c1c; font-size: 13px; }
.hms-confidential-denied { font-size: 11px; color: #92400e; background: rgba(217,119,6,.1); padding: 6px; border-radius: 6px; }
.hms-warn-red { background: rgba(220,53,69,.18); color: #b91c1c; border-color: rgba(220,53,69,.4); font-weight: 600; }

/* STATS */
.hms-stat-row { display: flex; flex-wrap: wrap; gap: 10px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 14px; flex: 1; min-width: 110px; }
.hms-stat-num { font-size: 18px; font-weight: 800; color: var(--hms-h); line-height: 1.2; }
.hms-stat-lbl { font-size: 11px; color: var(--hms-s); margin-top: 2px; }
.hms-stat-info { color: var(--hms-info); }
.hms-stat-success { color: var(--hms-success); }
.hms-stat-warning { color: var(--hms-warning); }
.hms-stat-danger { color: var(--hms-danger); }

/* TABS */
.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }

/* SECTION */
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 12px; }

/* CHIPS */
.hms-chip { background: var(--hms-bg2); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 4px 10px; font-size: 11px; font-weight: 600; cursor: pointer; }
.hms-chip.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }
.hms-chip i { margin-right: 3px; }

/* TIMELINE */
.hms-tl { display: flex; flex-direction: column; gap: 8px; }
.hms-tl-item { display: flex; gap: 12px; padding: 12px 14px; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; border-left: 4px solid var(--hms-primary); }
.hms-tl-icon { width: 36px; height: 36px; border-radius: 50%; background: var(--hms-primary-lt); color: var(--hms-primary); display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 18px; }
.hms-tl-body { flex: 1; min-width: 0; }
.hms-tl-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.hms-tl-title { color: var(--hms-h); font-weight: 600; font-size: 14px; }
.hms-tl-detail { color: var(--hms-s); font-size: 13px; margin-top: 4px; }

.tl-visit       { border-left-color: var(--hms-info); }
.tl-visit       .hms-tl-icon { background: rgba(23,162,184,.15); color: var(--hms-info); }
.tl-admission   { border-left-color: #9b59b6; }
.tl-admission   .hms-tl-icon { background: rgba(155,89,182,.15); color: #9b59b6; }
.tl-diagnosis   { border-left-color: var(--hms-danger); }
.tl-diagnosis   .hms-tl-icon { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.tl-prescription{ border-left-color: var(--hms-success); }
.tl-prescription .hms-tl-icon { background: rgba(40,167,69,.15); color: var(--hms-success); }
.tl-lab         { border-left-color: #34c38f; }
.tl-lab         .hms-tl-icon { background: rgba(52,195,143,.15); color: #34c38f; }
.tl-radiology   { border-left-color: #50a5f1; }
.tl-radiology   .hms-tl-icon { background: rgba(80,165,241,.15); color: #50a5f1; }
.tl-note        { border-left-color: var(--hms-primary); }
.tl-note        .hms-tl-icon { background: var(--hms-primary-lt); color: var(--hms-primary); }
.tl-disposition { border-left-color: var(--hms-warning); }
.tl-disposition .hms-tl-icon { background: rgba(255,193,7,.15); color: #ca8a04; }

/* TREND */
.hms-trend-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; }
.hms-trend { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px; }
.hms-trend-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 6px; }
.hms-trend-lbl { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
.hms-trend-now { color: var(--hms-h); font-size: 18px; font-weight: 800; }
.hms-trend-svg { width: 100%; height: 60px; }

/* TABLE */
.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 8px 10px; border-bottom: 1px solid var(--hms-br); text-align: left; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 8px 10px; border-bottom: 1px solid var(--hms-br); }

/* RECORDS */
.hms-list { display: flex; flex-direction: column; gap: 8px; }
.hms-rec { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 14px; cursor: pointer; transition: all .15s; }
.hms-rec:hover { border-color: var(--hms-primary); }
.hms-rec-title { color: var(--hms-h); font-weight: 600; font-size: 14px; margin-top: 4px; }

/* BADGES */
.hms-badge { padding: 2px 8px; border-radius: 50px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #ca8a04; }
.hms-badge-danger  { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }

.hms-acuity-pill { display: inline-flex; align-items: center; justify-content: center; min-width: 28px; height: 22px; border-radius: 6px; color: #fff; font-weight: 800; font-size: 11px; padding: 0 6px; }
.hms-acuity-pill.acu-1_immediate    { background: var(--acu-1); }
.hms-acuity-pill.acu-2_emergent     { background: var(--acu-2); }
.hms-acuity-pill.acu-3_urgent       { background: var(--acu-3); color: #1a1a1a; }
.hms-acuity-pill.acu-4_less_urgent  { background: var(--acu-4); }
.hms-acuity-pill.acu-5_non_urgent   { background: var(--acu-5); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-btn-icon { background: transparent; border: 1px solid var(--hms-br); color: var(--hms-s); border-radius: 6px; padding: 3px 7px; cursor: pointer; font-size: 13px; transition: all .15s; }
.hms-btn-icon:hover { color: var(--hms-primary); border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.08); }
.hms-btn-green { background: var(--hms-success); color: #fff; }
.hms-btn-green:hover:not(:disabled) { filter: brightness(1.08); }
.hms-btn-icon-green { color: var(--hms-success); border-color: rgba(40,167,69,.4); }
.hms-btn-icon-green:hover { background: rgba(40,167,69,.1); color: var(--hms-success); border-color: var(--hms-success); }
.hms-btn-red { background: var(--hms-danger); color: #fff; }
.hms-btn-red:hover:not(:disabled) { filter: brightness(1.08); }

.hms-danger-zone { border: 1px dashed var(--hms-br); border-radius: 10px; padding: 10px 14px; background: var(--hms-bg2); }
.hms-danger-toggle { background: transparent; border: none; color: var(--hms-s); cursor: pointer; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 1.2px; display: inline-flex; align-items: center; gap: 4px; padding: 0; }
.hms-danger-toggle:hover { color: var(--hms-danger); }
.hms-danger-body { margin-top: 14px; padding: 14px; background: rgba(220,53,69,.04); border: 1px solid rgba(220,53,69,.25); border-radius: 8px; }
.hms-danger-title { font-weight: 700; color: var(--hms-danger); margin-bottom: 6px; }
.hms-danger-desc { font-size: 12px; color: var(--hms-s); line-height: 1.5; margin-bottom: 12px; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 80px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 22px; width: 100%; max-width: 500px; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 17px; font-weight: 700; margin-bottom: 14px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-alert-amber  { background: rgba(245,158,11,.10); color: #b45309; border: 1px solid rgba(245,158,11,.35); }
.hms-check        { display: inline-flex; align-items: center; gap: 6px; color: var(--hms-h); font-size: 13px; cursor: pointer; }
.hms-check input  { accent-color: var(--hms-primary); }

.hms-leaflet-map  { width: 100%; height: 240px; border-radius: 10px; border: 1px solid var(--hms-br); overflow: hidden; }
.hms-suggest      { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 8px; max-height: 200px; overflow-y: auto; }
.hms-suggest-item { padding: 8px 12px; font-size: 13px; cursor: pointer; border-bottom: 1px solid var(--hms-br); color: var(--hms-h); }
.hms-suggest-item:last-child { border-bottom: none; }
.hms-suggest-item:hover { background: var(--hms-primary-lt); }

.hms-chip-input   { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; padding: 6px 10px; min-height: 38px; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; }
.hms-chip-input:focus-within { border-color: var(--hms-primary); }
.hms-chip         { display: inline-flex; align-items: center; gap: 4px; background: var(--hms-primary-lt); color: var(--hms-primary); border-radius: 50px; padding: 3px 4px 3px 10px; font-size: 12px; font-weight: 600; }
.hms-chip-x       { background: transparent; border: none; color: inherit; font-size: 18px; line-height: 1; cursor: pointer; padding: 0 6px; opacity: 0.7; }
.hms-chip-x:hover { opacity: 1; }
.hms-chip-text    { flex: 1; min-width: 140px; border: none; outline: none; background: transparent; color: var(--hms-h); font-size: 14px; padding: 4px; }
.hms-btn-link { background: none; border: none; color: var(--hms-primary); cursor: pointer; text-decoration: underline; font-size: inherit; }
.hms-text-danger { color: var(--hms-danger); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

const TIMELINE_TYPES = [
  { code: 'visit',        label: 'Visits',     icon: 'mdi-account-heart-outline' },
  { code: 'admission',    label: 'Admissions', icon: 'mdi-bed' },
  { code: 'diagnosis',    label: 'Diagnoses',  icon: 'mdi-clipboard-pulse' },
  { code: 'prescription', label: 'Rx',         icon: 'mdi-pill' },
  { code: 'lab',          label: 'Lab',        icon: 'mdi-flask' },
  { code: 'radiology',    label: 'Imaging',    icon: 'mdi-radioactive' },
  { code: 'note',         label: 'Notes',      icon: 'mdi-note-edit-outline' },
  { code: 'disposition',  label: 'Disposition',icon: 'mdi-check-decagram' }
];

const VITAL_METRICS = [
  { key: 'bp_systolic',       label: 'Systolic BP',  unit: 'mmHg', color: '#dc3545' },
  { key: 'bp_diastolic',      label: 'Diastolic BP', unit: 'mmHg', color: '#fd7e14' },
  { key: 'pulse',             label: 'Pulse',        unit: 'bpm',  color: '#1a7fe8' },
  { key: 'temperature',       label: 'Temperature',  unit: '°C',   color: '#9b59b6' },
  { key: 'oxygen_saturation', label: 'SpO₂',         unit: '%',    color: '#34c38f' },
  { key: 'respiratory_rate',  label: 'Resp Rate',    unit: '/min', color: '#17a2b8' },
  { key: 'blood_sugar',       label: 'Blood Sugar',  unit: 'mmol/L',color:'#e84393' },
  { key: 'weight',            label: 'Weight',       unit: 'kg',   color: '#16a085' },
  { key: 'news2_score',       label: 'NEWS2',        unit: '',     color: '#dc3545' }
];

export default {
  name: "PatientChart",
  components: { Navigation },
  data() {
    return {
      loading: true,
      patient: null,
      visits: [], admissions: [], diagnoses: [], prescriptions: [],
      labs: [], radiology: [], vitals: [], notes: [], invoices: [], insuranceSavings: null,

      // Per-category data-sharing consent
      dataConsents: [], consentLoading: false, consentSaving: "",

      // Visit-detail modal state
      showVisitDetail: false,
      visitDetail: null,
      visitDetailLoading: false,
      visitDetailError: null,
      visitRevealedSensitive: {}, // diagnosis_id -> true once revealed in this modal session

      // Reveal-reason modal (replaces window.prompt for sensitive reveals)
      revealPrompt: { open: false, label: '', reason: '', resolve: null },

      // Sensitive-info reveal state
      canRevealSensitive: false,
      sensitiveCategories: [
        { key: 'clinical_summary', label: 'Allergies / Chronic / Blood group', icon: 'mdi mdi-pulse' },
        { key: 'medications',      label: 'Current Medications',                icon: 'mdi mdi-pill' },
        { key: 'hiv',              label: 'HIV / ART',                          icon: 'mdi mdi-virus' },
        { key: 'mental_health',    label: 'Mental Health',                       icon: 'mdi mdi-brain' },
        { key: 'sgbv',             label: 'SGBV / GBV',                          icon: 'mdi mdi-shield-account' },
        { key: 'srh',              label: 'Sexual & Reproductive Health',        icon: 'mdi mdi-human-female-female' },
        { key: 'tb',               label: 'TB',                                  icon: 'mdi mdi-lungs' },
        { key: 'substance',        label: 'Substance Use',                       icon: 'mdi mdi-bottle-tonic' },
        { key: 'forensic',         label: 'Forensic / Medico-legal',             icon: 'mdi mdi-shield-key' }
      ],
      sensitiveOpen:       {}, // { hiv: true, ... }
      sensitiveData:       {}, // { hiv: {...}, ... }
      sensitiveTimers:     {}, // { hiv: 47 }   countdown per category
      sensitiveIntervals:  {}, // setInterval handles per category

      tab: 'timeline',
      timelineFilters: new Set(),
      timelineTypes: TIMELINE_TYPES,
      vitalMetrics: VITAL_METRICS,

      // Action state
      resendingWelcome: false,
      payLinkBusy: {},  // { invoice_id: true } while sending
      toastMsg: '', toastError: false, toastTimer: null,

      // Danger zone
      dangerOpen: false,
      confirmingDelete: false,
      deleteConfirmText: '',
      deleting: false,

      // Inline edit modal
      editOpen: false,
      editSaving: false,
      editError: '',
      editForm: {},
      bloodGroups: ['A+','A-','B+','B-','AB+','AB-','O+','O-','unknown'],
      insuranceProviders: ['SHA','Britam','Jubilee','AAR Insurance','Madison Insurance','CIC Insurance','UAP Old Mutual','APA Insurance','Heritage Insurance','ICEA Lion','Resolution Insurance','Liberty Life','Sanlam','Pacis Insurance','Saham Assurance','First Assurance','Trident Insurance','Geminia Life','Kenindia Assurance','Other'],

      // Allergy chip-picker state (edit modal)
      allergyTags: [],
      allergyInput: '',

      // Leaflet / address typeahead state (edit modal)
      addressQuery: '',
      addressSuggest: [],
      addressBusy: false,
      addressDebounce: null,
      _editMap: null,
      _editMarker: null,

      // Datalists
      countries: [
        'Kenya','Uganda','Tanzania','Rwanda','Burundi','South Sudan','Ethiopia','Somalia','Democratic Republic of the Congo',
        '— — — — — — — — — — — — — — — — —',
        'Afghanistan','Albania','Algeria','Angola','Argentina','Armenia','Australia','Austria','Bahrain','Bangladesh','Belarus','Belgium','Belize','Botswana','Brazil','Bulgaria','Burkina Faso',
        'Cameroon','Canada','Chad','China','Colombia','Comoros','Croatia','Cuba','Cyprus','Czechia',
        'Denmark','Djibouti','Egypt','Eritrea','Estonia','Eswatini','Fiji','Finland','France','Gabon','Gambia','Georgia','Germany','Ghana','Greece','Guinea',
        'India','Indonesia','Iran','Iraq','Ireland','Israel','Italy','Ivory Coast','Jamaica','Japan','Jordan','Kazakhstan','Kuwait','Latvia','Lebanon','Lesotho','Liberia','Libya','Lithuania','Luxembourg',
        'Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Mauritania','Mauritius','Mexico','Moldova','Mongolia','Morocco','Mozambique','Myanmar',
        'Namibia','Nepal','Netherlands','New Zealand','Niger','Nigeria','Norway','Oman','Pakistan','Panama','Philippines','Poland','Portugal','Qatar',
        'Romania','Russia','Saudi Arabia','Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia','South Africa','South Korea','Spain','Sri Lanka','Sudan','Sweden','Switzerland','Syria',
        'Tajikistan','Thailand','Togo','Tunisia','Turkey','Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan','Vietnam','Yemen','Zambia','Zimbabwe'
      ],
      commonAllergies: [
        'Penicillin','Amoxicillin','Ampicillin','Cephalexin','Ceftriaxone',
        'Sulfa drugs (sulfonamides)','Cotrimoxazole (Septrin)','Tetracycline','Doxycycline',
        'Erythromycin','Azithromycin','Ciprofloxacin','Metronidazole','Vancomycin',
        'Aspirin','Ibuprofen','Diclofenac','Naproxen','Paracetamol','Codeine','Morphine','Tramadol',
        'Insulin','Heparin','Warfarin','Phenytoin','Carbamazepine','Allopurinol',
        'Iodine','Iodinated contrast media','Local anaesthetic (Lignocaine)','General anaesthetic',
        'NSAIDs (general)','Quinine','Antimalarials (Sulfadoxine/Pyrimethamine)','Vaccine component',
        'Peanuts','Tree nuts','Cow milk / dairy','Eggs','Wheat / gluten','Soy','Fish','Shellfish','Sesame','Mustard','Sulfites','Mango','Tomato','Strawberry','Avocado','Kiwi','Pineapple','Chocolate',
        'Pollen','Grass pollen','Tree pollen','Dust mites','Mould spores','Cockroach','Cat dander','Dog dander','Smoke (tobacco)','Perfume / fragrance',
        'Bee sting','Wasp sting','Hornet sting','Fire ant','Mosquito (severe reaction)',
        'Latex','Nickel','Chromium','Formaldehyde','Hair dye (PPD)','Cosmetics','Detergents','Adhesives / glue','Rubber accelerators',
        'Snake antivenom','Blood / blood products','Sunlight (photosensitivity)','Cold (cold urticaria)','No known allergies'
      ]
    };
  },
  watch: {
    '$route.params.patientId'(id) { if (id) this.loadAll(id); },
    editOpen(val) {
      if (val) {
        this.$nextTick(() => this.initEditMap());
      } else {
        this.destroyEditMap();
        this.addressSuggest = [];
      }
    },
    allergyTags: {
      deep: true,
      handler(val) {
        if (this.editForm) this.editForm.allergies = val.join(', ');
      }
    }
  },
  computed: {
    initials() {
      if (!this.patient) return '?';
      var f = (this.patient.first_name || '').charAt(0);
      var l = (this.patient.last_name  || '').charAt(0);
      return ((f + l).toUpperCase()) || '?';
    },
    ageStr() {
      if (!this.patient || !this.patient.dob || this.patient.dob === '0000-00-00') return 'age unknown';
      var d = new Date(this.patient.dob); if (isNaN(d)) return '—';
      return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600000)) + 'y';
    },
    currentAttending() {
      // Prefer an active admission (no discharge date), else the most recent visit's doctor.
      var active = (this.admissions || []).find(function(a){ return !a.actual_discharge_date && (a.status === 'active' || !a.status); });
      if (active && active.doctor_name && active.doctor_name.trim()) return { name: active.doctor_name.trim(), context: 'Admitting · current admission' };
      var v = (this.visits || []).find(function(v){ return v.doctor_name && v.doctor_name.trim(); });
      if (v) return { name: v.doctor_name.trim(), context: 'Most recent visit' + (v.visit_date ? ' · ' + String(v.visit_date).slice(0,10) : '') };
      return null;
    },
    activeIssues() {
      return this.diagnoses.filter(d => (d.status || 'active') === 'active').length;
    },
    totalUnpaid() {
      return this.invoices.reduce((s, i) => s + (parseFloat(i.balance) || 0), 0);
    },
    vitalsAsc() {
      // For trend lines (oldest → newest)
      return [...this.vitals].sort((a, b) => new Date(a.recorded_at.replace(' ','T')) - new Date(b.recorded_at.replace(' ','T')));
    },
    timeline() {
      var events = [];
      // Visits
      this.visits.forEach(v => events.push({
        type: 'visit', at: v.arrival_time || v.visit_date,
        title: 'Visit · ' + (v.visit_type || ''), badge: v.visit_status, badgeClass: this.tlStatusBadge(v.visit_status, 'hms-badge-info'),
        detail: v.chief_complaint || '—', actor: v.doctor_name
      }));
      // Disposition events
      this.visits.filter(v => v.disposition && v.disposition_time).forEach(v => events.push({
        type: 'disposition', at: v.disposition_time,
        title: 'Disposition: ' + v.disposition, badge: null,
        detail: 'Visit ' + v.visit_number
      }));
      // Admissions
      this.admissions.forEach(a => {
        events.push({
          type: 'admission', at: a.admission_date,
          title: 'Admission · ' + (a.admission_type || ''), badge: a.status, badgeClass: 'hms-badge-purple',
          detail: a.diagnosis_on_admission || '—', actor: a.doctor_name
        });
        if (a.actual_discharge_date) {
          events.push({
            type: 'admission', at: a.actual_discharge_date,
            title: 'Discharged', badge: a.discharge_condition || 'discharged', badgeClass: 'hms-badge-success',
            detail: a.discharge_diagnosis || ''
          });
        }
      });
      // Diagnoses
      this.diagnoses.forEach(d => events.push({
        type: 'diagnosis', at: d.diagnosis_date || d.created_at,
        title: 'Diagnosis · ' + (d.icd_code || ''), badge: d.diagnosis_type, badgeClass: 'hms-badge-info',
        detail: d.diagnosis_name, actor: d.doctor_name
      }));
      // Prescriptions
      this.prescriptions.forEach(p => events.push({
        type: 'prescription', at: p.prescription_date || p.created_at,
        title: 'Prescription · ' + p.item_count + ' drug(s)', badge: p.status, badgeClass: this.tlStatusBadge(p.status, 'hms-badge-success'),
        detail: p.drugs || '—', actor: p.prescriber_name
      }));
      // Labs
      this.labs.forEach(l => events.push({
        type: 'lab', at: l.request_date || l.created_at,
        title: 'Lab request', badge: l.priority + ' · ' + l.status, badgeClass: this.tlStatusBadge(l.status, 'hms-badge-info'),
        detail: l.tests || '—', actor: l.doctor_name
      }));
      // Radiology
      this.radiology.forEach(r => events.push({
        type: 'radiology', at: r.created_at,
        title: 'Imaging · ' + r.scan_type, badge: r.priority + ' · ' + r.status, badgeClass: this.tlStatusBadge(r.status, 'hms-badge-info'),
        detail: r.body_part || '—', actor: r.doctor_name
      }));
      // Consultation notes
      this.notes.forEach(n => events.push({
        type: 'note', at: n.created_at,
        title: 'Clinical note (' + (n.note_type || 'soap') + ')',
        detail: this.summariseNote(n), actor: n.doctor_name
      }));

      events.sort((a, b) => new Date((b.at || '').replace(' ','T')) - new Date((a.at || '').replace(' ','T')));
      return events;
    },
    filteredTimeline() {
      if (!this.timelineFilters.size) return this.timeline;
      var self = this;
      return this.timeline.filter(e => self.timelineFilters.has(e.type));
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    var pid = this.$route.params.patientId || this.$route.query.patient;
    if (!pid) { this.$router.push('/patients'); return; }
    this.loadAll(pid);
    this.checkRevealPermission();
  },
  beforeUnmount() {
    var ints = this.sensitiveIntervals || {};
    for (var k in ints) { if (ints[k]) clearInterval(ints[k]); }
  },
  methods: {
    async checkRevealPermission() {
      try {
        var r = await executeJson({ function: "canRevealSensitive" }, hms);
        this.canRevealSensitive = !!(r.data && r.data.success && r.data.data && r.data.data.can);
      } catch(e) { this.canRevealSensitive = false; }
    },
    // Promise-returning replacement for window.prompt — pops the styled modal.
    askForRevealReason(label, defaultReason) {
      var self = this;
      return new Promise(function (resolve) {
        self.revealPrompt = {
          open: true,
          label: label || 'protected information',
          reason: defaultReason || 'Clinical review before treatment',
          resolve: resolve
        };
        // Focus the textarea after Vue paints
        self.$nextTick(function () {
          var el = self.$refs.revealReasonInput;
          if (el && el.focus) { el.focus(); el.select && el.select(); }
        });
      });
    },
    confirmReveal() {
      var r = (this.revealPrompt.reason || '').trim();
      if (!r) return;
      var resolve = this.revealPrompt.resolve;
      this.revealPrompt = { open: false, label: '', reason: '', resolve: null };
      if (resolve) resolve(r);
    },
    cancelReveal() {
      var resolve = this.revealPrompt.resolve;
      this.revealPrompt = { open: false, label: '', reason: '', resolve: null };
      if (resolve) resolve(null);
    },
    async onRevealCategory(key, label) {
      var reason = await this.askForRevealReason(label);
      if (reason == null) return;
      try {
        var r = await executeJson({ function: "revealSensitiveCategory", patient_id: this.patient.patient_id, category: key, reason: reason }, hms);
        if (!(r.data && r.data.success)) {
          this.showToast((r.data && r.data.message) || 'Access denied.', true);
          return;
        }
        var data = (r.data.data && r.data.data.data) || {};
        var ttl  = (r.data.data && r.data.data.auto_hide_seconds) || 60;
        this.sensitiveData       = Object.assign({}, this.sensitiveData,    { [key]: data });
        this.sensitiveTimers     = Object.assign({}, this.sensitiveTimers,  { [key]: ttl });
        this.sensitiveOpen       = Object.assign({}, this.sensitiveOpen,    { [key]: true });

        var self = this;
        if (this.sensitiveIntervals[key]) clearInterval(this.sensitiveIntervals[key]);
        this.sensitiveIntervals[key] = setInterval(function () {
          self.sensitiveTimers = Object.assign({}, self.sensitiveTimers, { [key]: (self.sensitiveTimers[key] || 0) - 1 });
          if ((self.sensitiveTimers[key] || 0) <= 0) self.onHideCategory(key);
        }, 1000);
      } catch (e) { this.showToast('Could not reveal: ' + e.message, true); }
    },
    onHideCategory(key) {
      if (this.sensitiveIntervals[key]) { clearInterval(this.sensitiveIntervals[key]); this.sensitiveIntervals[key] = null; }
      this.sensitiveOpen   = Object.assign({}, this.sensitiveOpen,   { [key]: false });
      this.sensitiveData   = Object.assign({}, this.sensitiveData,   { [key]: undefined });
      this.sensitiveTimers = Object.assign({}, this.sensitiveTimers, { [key]: 60 });
    },
    renderSensitiveBody(key) {
      var d = this.sensitiveData[key] || {};
      var esc = function(s) { if (s == null) return ''; return String(s).replace(/[&<>"']/g, function(m){return ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[m];}); };
      var kv = function(label, val) { return val != null && val !== '' ? '<div><strong>' + esc(label) + ':</strong> ' + esc(val) + '</div>' : ''; };
      if (key === 'clinical_summary') {
        return kv('Allergies', d.allergies) + kv('Chronic conditions', d.chronic_conditions) + kv('Blood group', d.blood_group);
      }
      if (key === 'medications') {
        var rx = d.prescriptions || []; var dp = d.dispensed || [];
        var rxHtml = rx.length
          ? '<div class="mb-1"><strong>Active prescriptions (90d):</strong></div>' + rx.map(function(x){
              return '<div>• ' + esc(x.drug_name || '—') + ' — ' + esc(x.dose || '') + ' ' + esc(x.frequency || '') + ' × ' + esc(x.duration || '') + ' <span class="hms-muted-sm">(' + esc((x.created_at || '').slice(0,10)) + ')</span></div>';
            }).join('')
          : '<div class="hms-muted-sm">No active prescriptions.</div>';
        var dpHtml = dp.length
          ? '<div class="mt-2 mb-1"><strong>Dispensed (90d):</strong></div>' + dp.slice(0, 10).map(function(x){
              return '<div>• ' + esc(x.drug_name || '—') + ' × ' + esc(x.qty_dispensed || '') + ' <span class="hms-muted-sm">(' + esc((x.dispensed_at || '').slice(0,10)) + (x.batch_no ? ', batch ' + esc(x.batch_no) : '') + ')</span></div>';
            }).join('')
          : '';
        return rxHtml + dpHtml;
      }
      if (key === 'hiv') {
        var a = d.art || {}; var p = d.pmtct || {};
        return (a.enrolment_date ? '<div><strong>ART:</strong> enrolled ' + esc(a.enrolment_date) + ' · regimen ' + esc(a.current_regimen || '—') + ' · VL ' + esc(a.viral_load_status || '—') + '</div>' : '<div>No ART record.</div>')
             + (p.enrolment_date ? '<div class="mt-1"><strong>PMTCT:</strong> ' + esc(p.cascade_stage || '—') + '</div>' : '');
      }
      if (key === 'mental_health') {
        var r = d.register || {}; var s = d.screens || [];
        return (r.diagnosis ? '<div><strong>Diagnosis:</strong> ' + esc(r.diagnosis) + ' (' + esc(r.severity || '—') + ')</div>' : '<div>No active register entry.</div>')
             + (s.length ? '<div class="mt-1"><strong>Recent screens:</strong> ' + s.map(function(x){return esc(x.screen_type) + '=' + esc(x.score);}).join(', ') + '</div>' : '');
      }
      if (key === 'sgbv') {
        var c = d.case || {};
        return c.case_id || c.incident_date ? '<div>Incident ' + esc(c.incident_date || '—') + ' · status ' + esc(c.case_status || '—') + '</div>' : '<div>No SGBV case on record.</div>';
      }
      if (key === 'srh') {
        var anc = d.anc; var fp = d.fp; var ipv = d.ipv;
        var out = '';
        if (anc) out += '<div><strong>ANC:</strong> LMP ' + esc(anc.lmp_date || '—') + ' · ' + esc(anc.gestation_weeks || '—') + 'w · G' + esc(anc.gravida || '?') + 'P' + esc(anc.parity || '?') + '</div>';
        if (fp)  out += '<div class="mt-1"><strong>FP:</strong> ' + esc(fp.method_code || '—') + ' since ' + esc(fp.started_at || '—') + '</div>';
        if (ipv) out += '<div class="mt-1"><strong>IPV screen:</strong> ' + esc(ipv.screened_at || '—') + ' · ' + (ipv.positive == 1 ? 'POSITIVE' : 'negative') + '</div>';
        return out || '<div>No SRH records.</div>';
      }
      if (key === 'tb') {
        var t = d.register || {};
        return t.registration_date ? '<div>Registered ' + esc(t.registration_date) + ' · type ' + esc(t.tb_type || '—') + ' · regimen ' + esc(t.regimen || '—') + '</div>' : '<div>No TB record.</div>';
      }
      if (key === 'substance') {
        var ss = d.screens || [];
        return ss.length ? '<div>' + ss.map(function(x){return esc(x.screen_type) + ': ' + esc(x.score) + (x.substance ? ' (' + esc(x.substance) + ')' : '');}).join(', ') + '</div>' : '<div>No substance-use screens.</div>';
      }
      if (key === 'forensic') {
        var cs = d.cases || [];
        return cs.length ? '<div>' + cs.map(function(x){return esc(x.case_no) + ' · ' + esc(x.case_type);}).join('<br>') + '</div>' : '<div>No forensic cases.</div>';
      }
      return '';
    },
    printDoc(type, id) { window.open(this.$router.resolve({ name: 'print', params: { type, id }, query: { auto: '1' } }).href, '_blank'); },
    showToast(msg, err) {
      this.toastMsg = msg; this.toastError = !!err;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ''; }, 2800);
    },
    copyText(text, label) {
      var val = (text === null || text === undefined) ? '' : String(text).trim();
      if (!val) { this.showToast('Nothing to copy', true); return; }
      var self = this;
      var done = function(){ self.showToast((label ? label + ' copied: ' : 'Copied: ') + val); };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(val).then(done).catch(function(){ self.fallbackCopy(val, done); });
      } else {
        this.fallbackCopy(val, done);
      }
    },
    fallbackCopy(val, done) {
      try {
        var ta = document.createElement('textarea');
        ta.value = val;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        done();
      } catch (e) {
        this.showToast('Copy failed — please select and copy manually', true);
      }
    },
    goEdit() {
      if (!this.patient) return;
      var p = this.patient;
      this.editForm = {
        first_name:           p.first_name           || '',
        last_name:            p.last_name            || '',
        other_names:          p.other_names          || '',
        dob:                  p.dob ? String(p.dob).slice(0,10) : '',
        gender:               p.gender               || '',
        phone:                p.phone                || '',
        email:                p.email                || '',
        national_id:          p.national_id          || '',
        blood_group:          p.blood_group          || 'unknown',
        allergies:            p.allergies            || '',
        chronic_conditions:   p.chronic_conditions   || '',
        address:              p.address              || '',
        country:              p.country              || 'Kenya',
        next_of_kin_name:     p.next_of_kin_name     || '',
        next_of_kin_phone:    p.next_of_kin_phone    || '',
        next_of_kin_relation: p.next_of_kin_relation || '',
        insurance_provider:   p.insurance_provider   || '',
        insurance_number:     p.insurance_number     || '',
        registration_source:  p.registration_source  || 'walk_in',
        // Referral block
        referral_source_facility:      p.referral_source_facility      || '',
        referral_source_type:          p.referral_source_type          || '',
        referral_source_level:         p.referral_source_level         || '',
        referral_source_county:        p.referral_source_county        || '',
        referral_source_contact_name:  p.referral_source_contact_name  || '',
        referral_source_contact_phone: p.referral_source_contact_phone || '',
        referral_source_reason:        p.referral_source_reason        || '',
        referral_arrival_condition:    p.referral_arrival_condition    || '',
        referral_source_date:          p.referral_source_date ? String(p.referral_source_date).replace(' ', 'T').slice(0,16) : '',
        referral_source_documentation: Number(p.referral_source_documentation || 0),
        referral_source_transport:     p.referral_source_transport     || '',
        referral_source_notes:         p.referral_source_notes         || '',
        // GPS
        latitude:  p.latitude  != null && p.latitude  !== '' ? Number(p.latitude)  : null,
        longitude: p.longitude != null && p.longitude !== '' ? Number(p.longitude) : null
      };
      this.allergyTags = (p.allergies || '').split(',').map(s => s.trim()).filter(Boolean);
      this.allergyInput = '';
      this.addressQuery = p.address || '';
      this.addressSuggest = [];
      this.editError = '';
      this.editOpen = true;
    },
    closeEdit() {
      if (this.editSaving) return;
      this.editOpen = false;
    },
    async saveEdit() {
      if (!this.editForm.first_name || !this.editForm.last_name) {
        this.editError = 'First name and last name are required.';
        return;
      }
      this.editSaving = true;
      this.editError = '';
      try {
        var payload = Object.assign({ function: 'updatePatient', patient_id: this.patient.patient_id }, this.editForm);
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.showToast('Patient updated.');
          this.editOpen = false;
          // Reload the chart so the identity card refreshes
          await this.loadAll(this.patient.patient_id);
        } else {
          this.editError = (r.data && r.data.message) || 'Update failed.';
        }
      } catch (e) {
        this.editError = 'Connection error.';
      }
      this.editSaving = false;
    },
    ageFromDob(dob) {
      if (!dob || dob === '0000-00-00') return '';
      var d = new Date(dob); if (isNaN(d.getTime())) return '';
      var t = new Date();
      var a = t.getFullYear() - d.getFullYear();
      var m = t.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
      return a;
    },
    onAgeInput(e) {
      var n = parseInt(e.target.value, 10);
      if (isNaN(n) || n < 0 || n > 130) { this.editForm.dob = ''; return; }
      var t = new Date();
      var y = t.getFullYear() - n;
      var mm = String(t.getMonth() + 1).padStart(2, '0');
      var dd = String(t.getDate()).padStart(2, '0');
      this.editForm.dob = y + '-' + mm + '-' + dd;
    },

    // ===== ALLERGY CHIPS =====
    addAllergyFromInput() {
      var v = (this.allergyInput || '').trim().replace(/,$/, '').trim();
      if (!v) return;
      if (!this.allergyTags.some(t => t.toLowerCase() === v.toLowerCase())) {
        this.allergyTags.push(v);
      }
      this.allergyInput = '';
    },
    removeAllergy(i) { this.allergyTags.splice(i, 1); },

    // ===== LEAFLET MAP (edit modal) =====
    initEditMap() {
      if (typeof window === 'undefined' || typeof window.L === 'undefined') {
        setTimeout(() => { if (this.editOpen) this.initEditMap(); }, 400);
        return;
      }
      var el = this.$refs.editAddressMap;
      if (!el) return;
      if (this._editMap) { try { this._editMap.remove(); } catch(e) {} this._editMap = null; this._editMarker = null; }
      var hasPin = this.editForm.latitude != null && this.editForm.longitude != null;
      var startLat = hasPin ? Number(this.editForm.latitude)  : -1.0833;
      var startLng = hasPin ? Number(this.editForm.longitude) :  35.8667;
      var startZoom = hasPin ? 16 : 12;
      var L = window.L;
      this._editMap = L.map(el, { zoomControl: true }).setView([startLat, startLng], startZoom);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap contributors' }).addTo(this._editMap);
      this._editMarker = L.marker([startLat, startLng], { draggable: true }).addTo(this._editMap);
      var self = this;
      this._editMarker.on('dragend', function(ev) {
        var ll = ev.target.getLatLng();
        self.editForm.latitude  = +ll.lat.toFixed(7);
        self.editForm.longitude = +ll.lng.toFixed(7);
      });
      this._editMap.on('click', function(ev) {
        self._editMarker.setLatLng(ev.latlng);
        self.editForm.latitude  = +ev.latlng.lat.toFixed(7);
        self.editForm.longitude = +ev.latlng.lng.toFixed(7);
      });
      setTimeout(() => { try { this._editMap.invalidateSize(); } catch(e) {} }, 250);
    },
    destroyEditMap() {
      if (this._editMap) { try { this._editMap.remove(); } catch(e) {} this._editMap = null; this._editMarker = null; }
    },
    setEditMapPin(lat, lng, zoom) {
      if (!this._editMap) return;
      this._editMap.setView([lat, lng], zoom || 16);
      if (!this._editMarker) {
        this._editMarker = window.L.marker([lat, lng], { draggable: true }).addTo(this._editMap);
      } else {
        this._editMarker.setLatLng([lat, lng]);
      }
      this.editForm.latitude  = +Number(lat).toFixed(7);
      this.editForm.longitude = +Number(lng).toFixed(7);
    },
    onAddressInput() {
      if (this.addressDebounce) clearTimeout(this.addressDebounce);
      if (!this.addressQuery || this.addressQuery.length < 3) { this.addressSuggest = []; return; }
      this.addressDebounce = setTimeout(() => { this.geocodeAddress(true); }, 500);
    },
    async geocodeAddress(suggestOnly) {
      var q = (this.addressQuery || '').trim();
      if (!q) { this.addressSuggest = []; return; }
      this.addressBusy = !suggestOnly;
      try {
        var url = 'https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=ke&q=' + encodeURIComponent(q);
        var resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
        var data = await resp.json();
        if (Array.isArray(data) && data.length) {
          if (suggestOnly) this.addressSuggest = data;
          else this.pickAddressSuggestion(data[0]);
        } else if (!suggestOnly) {
          this.showToast('No matches — drop a pin on the map.', true);
        }
      } catch (e) {
        if (!suggestOnly) this.showToast('Geocoder unreachable — drop a pin manually.', true);
      }
      this.addressBusy = false;
    },
    pickAddressSuggestion(s) {
      this.addressSuggest = [];
      this.addressQuery = s.display_name;
      this.editForm.address = s.display_name;
      this.setEditMapPin(parseFloat(s.lat), parseFloat(s.lon), 16);
    },
    useMyLocation() {
      if (!navigator.geolocation) { this.showToast('Geolocation not supported by browser.', true); return; }
      this.addressBusy = true;
      navigator.geolocation.getCurrentPosition((pos) => {
        this.setEditMapPin(pos.coords.latitude, pos.coords.longitude, 17);
        this.addressBusy = false;
      }, (err) => {
        this.showToast('Could not get GPS: ' + err.message, true);
        this.addressBusy = false;
      }, { enableHighAccuracy: true, timeout: 10000 });
    },
    async doResendWelcome() {
      if (!this.patient) return;
      if (!this.patient.phone && !this.patient.email) {
        this.showToast('No phone or email on file for this patient.', true);
        return;
      }
      this.resendingWelcome = true;
      try {
        var r = await executeJson({ function: 'resendPatientWelcome', patient_id: this.patient.patient_id }, hms);
        this.showToast((r.data && r.data.message) || (r.data && r.data.success ? 'Welcome message queued' : 'Failed'), !(r.data && r.data.success));
      } catch (e) { this.showToast('Connection error.', true); }
      this.resendingWelcome = false;
    },
    async doSendPayLink(inv) {
      if (!inv || !inv.invoice_id) return;
      this.payLinkBusy = Object.assign({}, this.payLinkBusy, { [inv.invoice_id]: true });
      try {
        var r = await executeJson({ function: 'sendInvoicePayLink', invoice_id: inv.invoice_id }, hms);
        this.showToast((r.data && r.data.message) || (r.data && r.data.success ? 'Pay link sent' : 'Failed'), !(r.data && r.data.success));
      } catch (e) { this.showToast('Connection error.', true); }
      var bs = Object.assign({}, this.payLinkBusy);
      delete bs[inv.invoice_id];
      this.payLinkBusy = bs;
    },
    payLinkUrl(token) {
      if (!token) return '';
      var base = (process.env.VUE_APP_BASE_URL_HMS || 'https://rcmsuat.craftsilicon.com/api1/hms').replace(/\/$/, '');
      return base + '/pay.php?t=' + token;
    },
    confirmDelete() {
      this.deleteConfirmText = '';
      this.confirmingDelete = true;
    },
    async doDelete() {
      if (!this.patient) return;
      if (this.deleteConfirmText !== this.patient.patient_no) return;
      this.deleting = true;
      try {
        var r = await executeJson({ function: 'deletePatient', patient_id: this.patient.patient_id }, hms);
        if (r.data && r.data.success) {
          this.showToast('Patient soft-deleted.');
          this.confirmingDelete = false;
          setTimeout(() => this.$router.push('/patients'), 800);
        } else {
          this.showToast((r.data && r.data.message) || 'Delete failed', true);
        }
      } catch (e) { this.showToast('Connection error.', true); }
      this.deleting = false;
    },
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    async loadAll(pid) {
      this.loading = true;
      var patientId = pid || this.$route.params.patientId;
      try {
        var r = await executeJson({ function: "getPatientChart", patient_id: patientId }, hms);
        if (r.data && r.data.success) {
          var d = r.data.data;
          this.patient       = d.Patient;
          this.visits        = d.Visits || [];
          this.admissions    = d.Admissions || [];
          this.diagnoses     = d.Diagnoses || [];
          this.prescriptions = d.Prescriptions || [];
          this.labs          = d.Labs || [];
          this.radiology     = d.Radiology || [];
          this.vitals        = d.Vitals || [];
          this.notes         = d.Notes || [];
          this.invoices      = d.Invoices || [];
        }
      } catch(e) { console.error(e); }

      // Pull the richer billing summary so we get per-invoice insurance savings + a lifetime rollup
      try {
        var b = await executeJson({ function: "getPatientBillingSummary", patient_id: patientId }, hms);
        if (b.data && b.data.success) {
          var bd = b.data.data;
          if (bd.invoices && bd.invoices.length) this.invoices = bd.invoices;
          this.insuranceSavings = bd.insurance_savings || null;
        }
      } catch(e) { /* keep invoices from getPatientChart */ }

      this.loading = false;
      this.loadConsents(patientId);
    },

    async loadConsents(pid) {
      this.consentLoading = true;
      try {
        var r = await executeJson({ function: "getPatientDataConsents", patient_id: pid }, hms);
        if (r.data && r.data.success) this.dataConsents = (r.data.data && r.data.data.Consents) || [];
      } catch(e) { /* non-fatal */ }
      this.consentLoading = false;
    },
    consentStatus(c) { return c.consent_status || 'granted'; },
    async setConsent(categoryCode, status) {
      var pid = this.patient ? this.patient.patient_id : this.$route.params.patientId;
      this.consentSaving = categoryCode;
      try {
        var r = await executeJson({ function: "setPatientDataConsent", patient_id: pid, category_code: categoryCode, consent_status: status, consent_method: 'verbal', given_relation: 'Self' }, hms);
        if (r.data && r.data.success) {
          var row = this.dataConsents.find(x => x.category_code === categoryCode);
          if (row) row.consent_status = status;
          this.showToast((status === 'restricted' ? 'Restricted: ' : 'Allowed: ') + categoryCode);
        } else { this.showToast((r.data && r.data.message) || 'Save failed', true); }
      } catch(e) { this.showToast('Connection error', true); }
      this.consentSaving = "";
    },

    toggleTimelineType(code) {
      if (this.timelineFilters.has(code)) this.timelineFilters.delete(code);
      else this.timelineFilters.add(code);
      this.timelineFilters = new Set(this.timelineFilters);
    },
    iconFor(type) {
      var t = TIMELINE_TYPES.find(x => x.code === type);
      return t ? t.icon : 'mdi-circle-medium';
    },
    // Patterns that mark a clinical note as sensitive (HIV / mental health / SRH / TB / SGBV / substance).
    // The Timeline preview redacts these unless the note has been explicitly revealed.
    noteIsSensitive(n) {
      var s = ((n.subjective || '') + ' ' + (n.objective || '') + ' ' + (n.assessment || '') + ' ' + (n.plan || '')).toLowerCase();
      var rx = /(\bhiv\b|\bart\b|\barv\b|arvs|antiretroviral|cd4\b|viral load|tdf|3tc|dtg|efavirenz|pmtct|aids|\bhei\b|\bphq[\s\-]?9\b|\bgad[\s\-]?7\b|epds|depress|anxiety|suicidal|self[\s\-]harm|psych|bipolar|schizophren|sertraline|fluoxetine|mirtazap|amitripty|olanzap|risperidon|lithium|diazepam|benzodiazep|\bsgbv\b|gender[\s\-]?based|sexual abuse|rape|defilement|pregnan|\banc\b|\bpnc\b|abortion|miscarri|fistula|\bipv\b|domestic violence|syphilis|gonorrh|chlamydia|herpes|hepatitis [bc]|\bhbsag\b|\brpr\b|\bvdrl\b|tubercu|\btb\b|\bmtb\b|geneexpert|gene[\s\-]?xpert|substance use|alcohol use disorder|opiate|cocaine|methamphet|cannabis use|cannabis[\s\-]?related|forensic|police case)/i;
      return rx.test(s);
    },
    summariseNote(n) {
      // If the SOAP body contains protected clinical info, never put it into the timeline preview.
      // Clinical staff who need to read it can open the visit detail modal where it's reveal-gated.
      if (this.noteIsSensitive(n)) {
        return '🔒 Clinical note contains protected information — open the visit to reveal (audit-logged).';
      }
      var bits = [];
      if (n.subjective) bits.push('S: ' + this.truncate(n.subjective, 80));
      if (n.assessment) bits.push('A: ' + this.truncate(n.assessment, 80));
      if (n.plan)       bits.push('P: ' + this.truncate(n.plan, 80));
      return bits.join(' · ') || '—';
    },

    latestValue(key) {
      for (var i = 0; i < this.vitals.length; i++) {
        var v = this.vitals[i][key];
        if (v != null && v !== '' && v !== 0) return v;
      }
      return '—';
    },
    sparkPoints(key, w, h) {
      var data = this.vitalsAsc.map(v => parseFloat(v[key])).filter(n => !isNaN(n) && n !== 0);
      if (data.length < 2) return '';
      var min = Math.min.apply(null, data);
      var max = Math.max.apply(null, data);
      var range = (max - min) || 1;
      var step = w / (data.length - 1);
      return data.map((d, i) => {
        var x = i * step;
        var y = h - ((d - min) / range) * (h - 8) - 4;
        return x + ',' + y;
      }).join(' ');
    },

    truncate(s, n) { return (s || '').length > n ? s.substring(0, n) + '...' : (s || ''); },
    formatNumber(n) { if (n == null) return '0'; return Number(n).toLocaleString(); },
    formatDate(s) { if (!s) return '—'; var d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleDateString(); },
    formatDateTime(s) { if (!s) return '—'; var d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleString(); },
    relativeTime(s) {
      if (!s) return ''; var d = new Date(s.replace ? s.replace(' ','T') : s); if (isNaN(d)) return '';
      var diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60)    return 'just now';
      if (diff < 3600)  return Math.floor(diff/60) + 'm ago';
      if (diff < 86400) return Math.floor(diff/3600) + 'h ago';
      if (diff < 604800)return Math.floor(diff/86400) + 'd ago';
      if (diff < 2592000) return Math.floor(diff/604800) + 'w ago';
      return Math.floor(diff/2592000) + 'mo ago';
    },
    acuityShort(l) { return ({ '1_immediate':'L1','2_emergent':'L2','3_urgent':'L3','4_less_urgent':'L4','5_non_urgent':'L5' })[l] || '?'; },
    admLos(a) {
      if (!a.admission_date) return 0;
      var s = new Date(a.admission_date.replace(' ','T'));
      var e = a.actual_discharge_date ? new Date(a.actual_discharge_date.replace(' ','T')) : new Date();
      return Math.max(0, Math.floor((e - s) / (24 * 3600 * 1000)));
    },
    admStatusBadge(s) { return ({ active:'hms-badge-info', discharged:'hms-badge-success', deceased:'hms-badge-warning' })[s] || 'hms-badge-neutral'; },
    diagStatusBadge(s) { return ({ active:'hms-badge-warning', resolved:'hms-badge-success', chronic:'hms-badge-purple' })[s] || 'hms-badge-warning'; },
    rxStatusBadge(s) { return ({ pending:'hms-badge-warning', dispensed:'hms-badge-success', partially_dispensed:'hms-badge-info', cancelled:'hms-badge-neutral' })[s] || 'hms-badge-neutral'; },
    tlStatusBadge(s, fallback) {
      var t = String(s || '').toLowerCase();
      if (['pending','sample_collected','processing','waiting','requested','scheduled','in_progress'].indexOf(t) >= 0) return 'hms-badge-warning';
      if (['completed','dispensed','resulted','verified','paid','discharged'].indexOf(t) >= 0) return 'hms-badge-success';
      if (['cancelled','rejected','failed'].indexOf(t) >= 0) return 'hms-badge-danger';
      return fallback || 'hms-badge-info';
    },
    priorityBadge(p) { return ({ stat:'hms-badge-danger', urgent:'hms-badge-warning', routine:'hms-badge-neutral' })[p] || 'hms-badge-neutral'; },
    invoiceBadge(s) { return ({ paid:'hms-badge-success', partial:'hms-badge-warning', unpaid:'hms-badge-danger', insurance_pending:'hms-badge-info', waived:'hms-badge-purple' })[s] || 'hms-badge-neutral'; },
    news2Badge(s) {
      if (s == null) return 'hms-badge-neutral';
      if (s >= 7) return 'hms-badge-danger';
      if (s >= 5) return 'hms-badge-warning';
      return 'hms-badge-success';
    },

    async openVisit(v) {
      this.visitDetail = null;
      this.visitDetailLoading = true;
      this.showVisitDetail = true;
      try {
        var r = await executeJson({ function: 'getPatientVisitDetail', visit_id: v.visit_id }, hms);
        if (r.data && r.data.success) this.visitDetail = r.data.data;
        else this.visitDetailError = r.data && r.data.message;
      } catch (e) { this.visitDetailError = e.message; }
      this.visitDetailLoading = false;
    },
    closeVisitDetail() { this.showVisitDetail = false; this.visitDetail = null; this.visitRevealedSensitive = {}; },
    visitGoConsultation(vid) { this.$router.push('/consultation?visit=' + vid); },
    async revealSensitiveDiagnosis(d) {
      var reason = await this.askForRevealReason(d.sensitive_category + ' diagnosis');
      if (reason == null) return;
      try {
        await executeJson({ function: 'revealSensitiveCategory', patient_id: this.patient.patient_id, category: d.sensitive_category, reason: reason }, hms);
      } catch (e) {}
      this.visitRevealedSensitive = Object.assign({}, this.visitRevealedSensitive, { [d.diagnosis_id]: true });
    },
    async revealSensitiveNote(n) {
      var reason = await this.askForRevealReason('protected clinical note');
      if (reason == null) return;
      try {
        await executeJson({ function: 'revealSensitiveCategory', patient_id: this.patient.patient_id, category: 'clinical_summary', reason: 'note_id=' + n.note_id + ' :: ' + reason }, hms);
      } catch (e) {}
      this.visitRevealedSensitive = Object.assign({}, this.visitRevealedSensitive, { ['note_' + n.note_id]: true });
    },
    async revealSensitiveImaging(r) {
      var reason = await this.askForRevealReason(r.sensitive_category + ' imaging');
      if (reason == null) return;
      try {
        await executeJson({ function: 'revealSensitiveCategory', patient_id: this.patient.patient_id, category: r.sensitive_category, reason: 'radiology_id=' + r.radiology_id + ' :: ' + reason }, hms);
      } catch (e) {}
      this.visitRevealedSensitive = Object.assign({}, this.visitRevealedSensitive, { ['rad_' + r.radiology_id]: true });
    },
    async revealSensitiveLab(lr) {
      var cats = (lr.sensitive_categories || []).join('/') || 'lab';
      var reason = await this.askForRevealReason(cats + ' lab result');
      if (reason == null) return;
      try {
        await executeJson({ function: 'revealSensitiveCategory', patient_id: this.patient.patient_id, category: (lr.sensitive_categories && lr.sensitive_categories[0]) || 'clinical_summary', reason: 'lab_request_id=' + lr.request_id + ' :: ' + reason }, hms);
      } catch (e) {}
      this.visitRevealedSensitive = Object.assign({}, this.visitRevealedSensitive, { ['lab_' + lr.request_id]: true });
    }
  }
};
</script>
